import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { keyed } from 'lit/directives/keyed.js';
import { Router } from '@lit-labs/router';
import { clearLegacyClient, applyLibraryFromURL, ensureLegacyClientAutoPicked, hasPendingLibraryParam, legacyClientId, LEGACY_CLIENT_EVENT, pickLegacyClient } from "./services/legacy-client.js";
import { isCommunicoStaffLevel, isPartnerSidePath, libraryGateBlocks } from './navigation/library-gate.js';
import { consumeReportFocusParam, hasPendingReportParam } from './services/retest-report.js';
import { api } from './services/api.js';
import { captureRedirectAfterLogin, LOGIN_REDIRECT_KEY } from './services/login-redirect.js';
import type { ReserveBookingFilters, ReserveRebookPrefill, RoomSelection } from './types/reserve.js';
import { libraryClientId } from './library/context.js';
import { libraryLookupForCurrentLibrary } from './services/terminology.js';
import { CARDHOLDER_TERM_CHANGED_EVENT } from './library/client-event.js';
import { activeCardholderTerm, setActiveCardholderTerm } from './utils/terminology.js';
import { DATE_ORDER_CHANGED_EVENT, getDateOrder, setDateOrder, setDateOrderFromCountry } from './utils/date-order.js';
import { sidePanelManager } from './panel/manager.js';
import { showToast } from './utils/toast.js';
import { setSessionUser } from './utils/sessionUser.js';
import { rememberAddress } from './utils/previousAddress.js';
import { bootstrapTheme, bindThemeChangeListener, applyUserTheme, osDefaultTheme, normalizeTheme } from './services/theme.js';
import { applySidebarWidth, isSidebarWidth, type SidebarWidth } from './services/sidebar.js';
import { navigationGuard } from './navigation/guard.js';
import { decideChunkRecovery } from './utils/chunkRecovery.js';
import { resolveLandingPath } from './utils/actionCenterFilters.js';
import { currentEntryHash, parseEntryHash, isUpdateAvailable, shouldPromptReload, loadDismissedHash, saveDismissedHash } from './utils/appVersion.js';
import { routerPathOnly } from './utils/routerPath.js';
import { thriveRedirectFor } from './thrive/redirects.js';
// Responsive Workstream B (roam-responsive-pwa-spec.md §7) — the ONE
// place a route's capability gate is enforced. ViewportController is
// the shared shell-level tier source (Workstream A); capabilityFor is
// the form-factor registry's pure resolver.
import { ViewportController } from './design-system/viewport-controller.js';
import { capabilityFor, deviceHandoffLabel, type CapabilityLevel } from './responsive/capability-registry.js';
// Eager — a gate can fire on ANY route, so this can't ride a lazy chunk
// tied to whichever page happens to be gated (the same reasoning as the
// eager cos-not-found/cos-error-state imports below).
import './design-system/cos-needs-desktop.js';
import { registerServiceWorker } from './services/serviceWorker.js';

// Guards the chunk-failure reload against looping. sessionStorage, never
// localStorage (house rule) — the state is meaningless across tabs.
const CHUNK_RELOAD_KEY = 'cos.chunk_reload';

// Proactive stale-deploy polling cadence: every 5 min, plus a throttled
// check (min 60s apart) on tab refocus and on navigation — the moment
// just before the user would hit a dead chunk.
const VERSION_POLL_MS = 5 * 60 * 1000;
const VERSION_MIN_GAP_MS = 60 * 1000;

import './components/cos-app-shell.js';
import './components/cos-login.js';
// Stage 36b — cos-partner-detail moved to lazy-load via _lazyEnter
// (see /partners/:id route below). The eager import that used to
// live here pulled the 910+-line component into the entry bundle.
//
// bundle-entry — cos-dashboard-detail, cos-partner-list, cos-ticket-list,
// cos-project-list, cos-project-detail, cos-implementation-overview,
// cos-implementation-module, cos-kb-list, cos-kb-article all moved to
// lazy-load via _lazyEnter (see their routes below). They were the
// heaviest remaining eager route components (90+ KB each in cos-ticket-
// list's case) and pushed the entry chunk over the bundle-size-gate
// ceiling. None of the nine is referenced outside app.ts's own route
// table (verified by grep), so there's no other render path to break.
//
// Stage 34c — cos-ticket-detail, cos-ticket-create, cos-kb-editor are
// lazy-loaded per route (see _lazyEnter wiring below). The Stage 28c
// pattern was set by cos-admin-*; this extends it to the heaviest
// remaining eager components. cos-ticket-detail pulled cos-ai-triage-
// panel + cos-ai-draft-card into the entry bundle; cos-ticket-create
// pulled cos-ai-quality-feedback. Both move out automatically with
// their parents.
// cos-kb-versions + cos-kb-editor lazy-loaded via route enter
// (Stage 28c for versions; Stage 34c for editor).
import './components/cos-not-found.js';
import { SUPPORT_EMAIL_PAGE_ENABLED } from './utils/supportEmailSwitch.js';
import { lastLibraryId } from './desk/library.js';
import { clearCachedMe, isConnectionFailure, readCachedMe, writeCachedMe } from './services/me-cache.js';
// Eager — the chunk-failure error frame can never depend on a chunk
// that just failed. (Used to ride in for free via cos-kb-list's eager
// import; now imported explicitly since cos-kb-list moved to lazy.)
import './components/cos-error-state.js';
// /admin's empty-selection prompt (2026-07-30).
import './design-system/cos-empty-state.js';
import { renderAdminsOnly } from './preserve/notAvailable.js';
import { LIBRARY_ADMIN_PRODUCTS, LIBRARY_LEVEL2_PRODUCTS } from './partner-admin/catalog.js';

// Stage 28c — admin surfaces and KB version history are lazy-loaded
// per route. Staff/non-admin users never hit /admin/*; admins hit it
// rarely. Each route's `enter` resolves the dynamic import before
// the render function fires, so the custom-element registration is
// in place by the time html`<cos-admin-foo>` renders. cos-kb-versions
// gets the same treatment because version history is browsed
// infrequently relative to article reads.
//
// Vite splits each dynamic import into its own chunk named after the
// component file. Caddy serves them at /assets/<chunk>.js on demand.
// Sub-100 kB chunks on local network feel instant; no Suspense
// fallback needed.

interface User {
	id: number;
	email: string;
	first_name?: string;
	last_name?: string;
	role: string;
	level: number;
	partner_id?: number | null;
	landing_page?: string; // 'inbox' (staff-only) | 'dashboard' | 'action-center'
	// Optional: an older cached payload or a failed parse must read as NO
	// access, not as "unknown, let it through". canAccessBilling() below
	// treats anything other than a literal true as false.
	can_access_billing?: boolean;
	// False for a library login whose user row names no library; the
	// backend refuses every other route for it (code library_not_linked).
	// Only a literal false shows the not-linked page, so an older payload
	// without the field behaves as before.
	library_linked?: boolean;
}

// A library login with no linked library gets one page instead of the app
// (components/cos-library-not-linked.ts). Strict false: see User above.
function isUnlinkedLibraryLogin(u: User | null): boolean {
	return !!u && u.library_linked === false;
}

// Stage 20 — admin role gate, mirrored from backend AdminRoles. Lives
// here (rather than a shared util) because the only client-side admin
// gate is route-level; deep in the page tree the backend returns 403.
// Stage 49 — super_admin sits ABOVE admin and must keep every admin surface.
const ADMIN_ROLES = new Set(['admin', 'executive', 'super_admin']);
function isAdmin(u: User | null): boolean {
	return !!u && u.level === 1 && ADMIN_ROLES.has(u.role);
}

// Every Preserve address: the staff surface and each of its routed pages.
function isPreserveAdminsOnlyPath(path: string): boolean {
	return path === '/preserve' || path.startsWith('/preserve/');
}

// A library's own admin: level 3, signed in to a library. Library admins set
// Roster up (Paul, 31 October 2026) and Preserve up (1 November 2026) for
// their own library on /partner-admin, and see only those sections there; the backend scopes every one of them.
function isLibraryAdmin(u: User | null): boolean {
	return !!u && u.level === 3 && !!u.partner_id && u.library_linked !== false;
}

// A linked level 2 is pinned to its own library by the Preserve routes exactly
// like a level 3 admin (server preserveScope), so it reaches the Preserve
// setup sections on /partner-admin, and only those: Roster's routes are level 3.
function isLinkedLevel2(u: User | null): boolean {
	return !!u && u.level === 2 && !!u.partner_id && u.library_linked !== false;
}

// The feedback DISPATCHER — who decides which reports get worked on, as
// opposed to who can read and describe them (isAdmin, above). Mirrors
// isFeedbackDispatcher() in the backend handler, which is the real gate;
// this only keeps the console from offering a button that would 403.
function isDispatcher(u: User | null): boolean {
	return !!u && u.level === 1 && u.role === 'super_admin';
}

// RequireBillingAccess in the backend swapped the level-1 gate for a
// hardcoded named allowlist; middleware.CanAccessBilling is the single
// source of truth and /auth/me now serves its verdict as one flag. Strict
// equality to true so a missing field (stale cache, failed parse) reads as
// no access rather than as "unknown, let it through".
function canAccessBilling(u: User | null): boolean {
	return u?.can_access_billing === true;
}


/**
 * <cos-app> — root shell. Owns:
 *
 *   - the @lit-labs/router instance and route table
 *   - the auth-event plumbing (login/logout/expired → redirect)
 *   - the link-click interceptor that keeps navigation client-side
 *
 * Authenticated routes are wrapped in <cos-app-shell>. /login renders
 * raw — no shell, no nav.
 */
@customElement('cos-app')
export class CosApp extends LitElement {
	@state() private _user: User | null = null;
	@state() private _path = window.location.pathname;
	// Mirrors legacy-client.ts's sessionStorage pick as reactive state, kept
	// in sync off LEGACY_CLIENT_EVENT (see _onLegacyClientChanged below).
	// `_shell()` reads THIS, never a fresh legacyClientId() call, because a
	// bare call reads the live value but doesn't make anything re-render:
	// picking a library from the Partner-nav rail fired the event and
	// cos-app-shell's OWN `_legacyClientId` state updated it (removing its
	// "Choose a library" prompt), but CosApp itself never listened, so it
	// never re-ran `_shell()`, so the gated page was never constructed and
	// the shell's <slot> stayed empty, blank content, no console error,
	// fixed only by a second navigation forcing `outlet()` to re-render.
	// This state field is what makes picking a library re-render CosApp.
	@state() private _legacyClientId = legacyClientId();
	// The library's own word for the people it serves (cardholder_terminology),
	// as a plural. Module-level copy (the admin catalog, Thrive registry,
	// display labels) reads activeCardholderTerm() at render time and has no
	// controller of its own to redraw it, so the word is resolved before a
	// page mounts (_lazyEnter) and the partner-side page is remounted once if
	// a library swap turns out to change it (remountKey in _shell).
	@state() private _cardholderPlural = activeCardholderTerm().plural;
	// The active date order. It joins the page's remount key, the one place a
	// screen already open redraws its dates when the library's order changes.
	@state() private _dateOrder = getDateOrder();
	private _userPromise: Promise<void> | null = null;
	// The location the router has actually committed to. Tracked so a
	// cancelled browser back/forward (popstate fires AFTER the URL has
	// already changed) can be restored — see `_onPopState`.
	private _committedHref = window.location.pathname + window.location.search;

	// A8 — in-flight session-restore attempt, shared by every guard.
	private _sessionPromise: Promise<boolean> | null = null;

	// A8 (partner round 1, Marcie) — "open in a new tab keeps me signed in".
	//
	// A fresh tab has no access token (sessionStorage is tab-scoped), so every
	// guard used to bounce straight to /login. The refresh cookie is httpOnly
	// and shared across tabs, so we try ONE silent refresh first and only fall
	// back to /login if there's no usable cookie. Awaited inside the (already
	// async) guards, so the route simply doesn't commit until it resolves —
	// no login flash, no new render state.
	//
	// The promise memoises only the IN-FLIGHT attempt (cleared on settle):
	// concurrent guards share one refresh, but a later navigation — notably
	// after logout, which clears the token — re-evaluates instead of reusing
	// a stale `true`.
	private _ensureSession(): Promise<boolean> {
		if (api.isAuthenticated()) return Promise.resolve(true);
		if (!this._sessionPromise) {
			this._sessionPromise = api.restoreSession()
				.then(ok => {
					if (ok) this._onSessionRestored();
					return ok;
				})
				.finally(() => { this._sessionPromise = null; });
		}
		return this._sessionPromise;
	}

	// The re-test link's library switch (utils/retestTarget.ts) arrives as
	// ?lib=<id> in the URL, never in sessionStorage the link itself wrote —
	// the link is now a same-tab navigation (retestLinkView.ts), but the
	// pick still has to travel through the URL rather than a click handler
	// writing straight to sessionStorage, because a route's own data fetch
	// can start mounting before a fire-and-forget write would land. This
	// waits for a real session before resolving the id against the user's
	// OWN permitted client list — if there isn't one yet (a cold tab with no
	// token), it leaves the param untouched: captureRedirectAfterLogin will
	// have captured it as part of the redirect target, and this method runs
	// again, successfully, once signed in.
	//
	// Called from EVERY route guard (_authGuard/_adminGuard/_staffOnlyGuard,
	// plus /partners' own bespoke enter below), each time as the LAST step
	// before the guard resolves `true` — never from connectedCallback. That
	// used to fire this fire-and-forget, in parallel with the router's own
	// first enter() for whatever route was already loading: nothing forced
	// one to finish before the other, so a destination page's first data
	// fetch could beat the library switch to the punch and read the WRONG
	// library out of sessionStorage. Resolving inside the guard means the
	// route (and therefore the destination component's connectedCallback)
	// cannot mount until this has already run — deterministic order, not a
	// race two copies of the same call happened to usually win.
	private async _applyLibraryFromURL(): Promise<void> {
		// Skip the session-and-fetch round trip entirely on the (overwhelming)
		// majority of boots that never carry this param — no silent-refresh
		// attempt should fire just because the app started.
		if (!hasPendingLibraryParam()) return;
		if (!(await this._ensureSession())) return;
		if (!this._user && this._userPromise) await this._userPromise;
		await applyLibraryFromURL();
	}

	// The library gate (Paul, 15 September 2026: "you should not be able to
	// open anything until you have chosen a library"). `_shell` (below)
	// reads legacyClientId() synchronously to decide whether to construct
	// the destination page at all, so that decision needs no await of its
	// own. But a Communico staffer with exactly ONE available library is
	// auto-picked into it (ensureLegacyClientAutoPicked in
	// services/legacy-client.ts), and that pick has to have already landed
	// by the time `_shell` checks, or a single-client staffer would see the
	// "Choose a library" prompt flash and clear itself a moment later.
	//
	// Same ordering argument as _applyLibraryFromURL just above, and the
	// same place: every guard, right before it resolves `true`, so the
	// destination component can't mount until this has already resolved.
	// Mirrors legacy-client.ts's sessionStorage pick into `_legacyClientId`
	// (declared above) so choosing or swapping a library re-renders CosApp
	// itself, not just cos-app-shell (which keeps this same mirror for its
	// own chrome). `e.detail?.id` matches cos-app-shell's own handler.
	private _onLegacyClientChanged = (e: CustomEvent<{ id: number }>): void => {
		this._legacyClientId = e.detail?.id ?? legacyClientId();
		void this._loadCardholderTerm();
	};

	// The Library page saved a new word: pick it up (already primed in the
	// cache) so the shell, the nav and the partner-side page redraw in it.
	private _onCardholderTermChanged = (): void => {
		void this._loadCardholderTerm();
	};

	private _onDateOrderChanged = (): void => {
		this._dateOrder = getDateOrder();
	};

	// Resolves the current library's word (cached per library for the
	// session) and makes it the active one. Bounded so a slow answer never
	// holds a page: past the wait the page mounts with the word it has, and
	// _cardholderPlural remounts it when the answer lands. Any failure keeps
	// the default; a word never breaks a page.
	private _termLookupSeq = 0;

	private async _loadCardholderTerm(): Promise<void> {
		try {
			// The same answer carries the library's country, which sets the one
			// date order every date box reads (utils/date-order.ts). A library
			// whose country is unknown, or no library chosen, reads month first.
			// A slow answer for the library picked before this one must not
			// overwrite the current library's word or date order, so each
			// lookup is numbered and only the latest may apply its result.
			const seq = ++this._termLookupSeq;
			const pending = libraryLookupForCurrentLibrary().then(({ term, country, dateOrder }) => {
				if (seq !== this._termLookupSeq) return;
				setActiveCardholderTerm(term);
				// The library's own date format wins over its country; an older
				// server answer carries no order, so the country decides.
				if (dateOrder) setDateOrder(dateOrder);
				else setDateOrderFromCountry(country);
				this._cardholderPlural = term.plural;
			});
			await Promise.race([pending, new Promise((resolve) => setTimeout(resolve, 1500))]);
		} catch {
			/* keep the word already showing */
		}
	}

	private async _ensureLibraryChosen(): Promise<void> {
		const level = this._user?.level ?? 1;
		if (!isCommunicoStaffLevel(level)) return; // library staff have their own tenant
		if (!isPartnerSidePath(this._path, window.location.search)) return;
		if (legacyClientId() !== 0) return; // already chosen, nothing to resolve
		try {
			await ensureLegacyClientAutoPicked();
		} catch {
			// The library list is a live read. On /desk, a PC that Desk last loaded online for a
			// library can still start for that library from its saved copy, so use it rather than
			// holding the gate up; Desk itself says what is missing if the PC is not set up for it.
			// Anywhere else, the gate simply stays up until the picker loads.
			const last = lastLibraryId();
			if (last > 0 && this._path.startsWith('/desk')) pickLegacyClient(last);
		}
	}

	// The re-test link's "come back to the report" leg (utils/retestTarget.ts
	// / retestLinkView.ts). Delivered to <cos-app-shell> as a property (never
	// re-derived from the URL there) — an OBJECT, not a bare number, because
	// the same report id can legitimately be re-tested twice in a row from
	// the SAME page (the panel is omnipresent; nothing stops someone from
	// reopening it and clicking the identical link again), and a repeated
	// primitive value wouldn't register as "changed" under Lit's default
	// reference-equality diffing. A fresh object every capture always does.
	@state() private _pendingReportFocus: { id: number } | null = null;

	// Reads + ALWAYS STRIPS `?retest_report=<id>` synchronously — called from
	// EVERY route guard, in the exact same spot as _applyLibraryFromURL()
	// (right before the guard resolves `true`), so this resolves before the
	// destination component has even been constructed, let alone mounted.
	//
	// That timing is load-bearing, not just tidy: /attend's own listState.ts
	// treats the URL query as its canonical view state and REWRITES it
	// (history.replaceState, dropping anything it doesn't recognise) the
	// moment cos-attend-events connects. An earlier version of this feature
	// stripped the param from cos-app-shell's connectedCallback — which only
	// fires ONCE, when the shell first mounts, never again on an in-app SPA
	// navigation (the shell is a persistent element the router re-renders
	// through, not remounted per route) — so on every subsequent in-app
	// click the param survived all the way down into the destination page,
	// which then ate it before cos-app-shell ever got a chance to see it.
	// Stripping it here, inside the guard, happens before @lit-labs/router's
	// goto() even calls the matched route's render() — there is no
	// destination component alive yet to race against.
	//
	// Also fires for a navigation to the SAME path the user is already on:
	// @lit-labs/router's goto() re-runs the matched route's enter() on every
	// call, unconditionally — it has no "already there" short-circuit (see
	// node_modules/@lit-labs/router's Routes.goto — it re-executes
	// route.enter(params) regardless of whether pathname actually changed).
	// Re-testing a report filed from the very page you're currently on is a
	// real path (the panel floats over every page), so this can't lean on
	// "the path changed" as its trigger — it runs unconditionally inside the
	// guard instead, on every navigation, matching path or not.
	private _captureReportFocusFromURL(): void {
		if (!hasPendingReportParam()) return;
		const id = consumeReportFocusParam();
		if (id !== null) this._pendingReportFocus = { id };
	}

	// cos-app-shell received _pendingReportFocus via the property binding
	// as part of THIS SAME render (Lit commits property bindings on a
	// child before that child's own — later, async — update cycle runs),
	// so it's already been delivered by the time this fires. Clearing it
	// here (rather than leaving it set forever) matters for exactly one
	// case: signing out unmounts <cos-app-shell> entirely (the /login
	// route renders without _shell()'s wrapper), and signing back in
	// mounts a BRAND NEW instance whose own default is null — without
	// this reset, that fresh instance would see this leftover non-null
	// value as newly arrived and replay an already-consumed report's
	// panel into a different session.
	protected updated(changed: Map<string, unknown>): void {
		if (changed.has('_user')) setSessionUser(this._user);
		if (changed.has('_pendingReportFocus') && this._pendingReportFocus !== null) {
			this._pendingReportFocus = null;
		}
	}

	// The authed-boot work connectedCallback does when a token was already in
	// storage — re-run for a session that only materialised after the refresh.
	private _onSessionRestored(): void {
		if (!this._userPromise) this._userPromise = this._restoreUser();
		void bootstrapTheme();
		void this._applyStoredTheme();
	}

	// Auth guard runs as the @lit-labs/router `enter` callback per route.
	// Returning false cancels the navigation; the redirect to /login is
	// scheduled via Router.goto so we don't mutate route state mid-render.
	//
	// On a deep-link refresh (or initial load with a token in storage)
	// we await the in-flight /auth/me fetch so child routes mount with
	// _user populated. Without this, routes briefly render with the
	// fallback level=1, fire level-1-only requests, and 403.
	private _authGuard = async (): Promise<boolean> => {
		if (!(await this._ensureSession())) {
			// Offline Desk: a tab with no sign-in and no connection cannot be signed in, but /desk can
			// say so and keep a registered PC's unsent actions safe; /login could not even load.
			if (api.refreshFailedOnNetwork && window.location.pathname.startsWith('/desk')) {
				// With nobody signed in the library gate would treat the tab as Communico staff and ask
				// for a library it cannot load. The library Desk last ran for on this PC is the only
				// one it can offer, unlock or warn about, so start there.
				const last = lastLibraryId();
				if (legacyClientId() === 0 && last > 0) pickLegacyClient(last);
				return true;
			}
			queueMicrotask(() => {
				captureRedirectAfterLogin(); if (window.location.pathname !== '/login') this._redirect('/login');
			});
			return false;
		}
		if (!this._user && this._userPromise) {
			await this._userPromise;
		}
		await this._applyLibraryFromURL();
		this._captureReportFocusFromURL();
		await this._ensureLibraryChosen();
		return true;
	};

	// Stage 20 — admin guard for /admin/* routes. Bounces non-admin
	// users to /dashboard with a toast. Defence in depth on top of the
	// backend RequireAdminRole gate; either alone is sufficient.
	private _adminGuard = async (): Promise<boolean> => {
		if (!(await this._ensureSession())) {
			queueMicrotask(() => {
				captureRedirectAfterLogin(); if (window.location.pathname !== '/login') this._redirect('/login');
			});
			return false;
		}
		if (!this._user && this._userPromise) {
			await this._userPromise;
		}
		if (!isAdmin(this._user)) {
			queueMicrotask(() => {
				this._redirect('/dashboard');
				showToast('Admin access required.', 'warn', 4000);
			});
			return false;
		}
		await this._applyLibraryFromURL();
		this._captureReportFocusFromURL();
		await this._ensureLibraryChosen();
		return true;
	};

	// /partner-admin: Communico admins, as _adminGuard, plus a library's own
	// admin, who the page limits to the Roster sections (isLibraryAdmin).
	private _partnerAdminGuard = async (): Promise<boolean> => {
		if (!(await this._ensureSession())) {
			queueMicrotask(() => {
				captureRedirectAfterLogin(); if (window.location.pathname !== '/login') this._navigate('/login');
			});
			return false;
		}
		if (!this._user && this._userPromise) {
			await this._userPromise;
		}
		if (!isAdmin(this._user) && !isLibraryAdmin(this._user) && !isLinkedLevel2(this._user)) {
			queueMicrotask(() => {
				this._navigate('/dashboard');
				showToast('Admin access required.', 'warn', 4000);
			});
			return false;
		}
		await this._applyLibraryFromURL();
		this._captureReportFocusFromURL();
		await this._ensureLibraryChosen();
		return true;
	};

	// Staff-only guard for routes that don't make sense for level-3 users
	// (Projects list/detail). Bounces them back to /dashboard.
	private _staffOnlyGuard = async (): Promise<boolean> => {
		if (!(await this._ensureSession())) {
			queueMicrotask(() => {
				captureRedirectAfterLogin(); if (window.location.pathname !== '/login') this._redirect('/login');
			});
			return false;
		}
		if (!this._user && this._userPromise) {
			await this._userPromise;
		}
		if (this._user && this._user.level !== 1) {
			queueMicrotask(() => this._redirect('/dashboard'));
			return false;
		}
		await this._applyLibraryFromURL();
		this._captureReportFocusFromURL();
		await this._ensureLibraryChosen();
		return true;
	};

	// Stage 76, opened up per the Action Center plan Part 5 (O1) — the
	// per-user home page. Staff (level 1) keep the full 3-way choice
	// (inbox/dashboard/action-center, default inbox); partner-tier
	// (level >= 3) now gets a choice too, between dashboard and
	// action-center — the inbox stays a staff-only triage surface. Logic
	// lives in a pure helper (utils/actionCenterFilters.ts) so it's
	// unit-testable without mounting the whole app shell.
	private _landingPath = (): string => resolveLandingPath(this._user);

	// Stage 28c — wrap a guard + dynamic component import so the
	// route's chunk is fetched on first navigation. The guard runs
	// first (a 403 redirect costs nothing); the import only fires
	// for users who pass. Returning a Promise keeps the existing
	// `enter: Promise<boolean>` contract that `_authGuard`/
	// `_adminGuard`/`_staffOnlyGuard` already satisfy.
	//
	// Stage 41b-2.1 — accepts an optional human label so the
	// chunk-loading overlay can say what's loading. When set,
	// `_chunkLoading` toggles a fixed overlay; the await yields a
	// microtask so the render fires before the network round-trip.
	private _lazyEnter = (
		guard: () => Promise<boolean>,
		loader: () => Promise<unknown>,
		label?: string,
	) => async (): Promise<boolean> => {
		const allowed = await guard();
		if (!allowed) return false;
		await this._loadCardholderTerm();
		if (label) {
			this._chunkLoading = label;
			// Yield so the overlay renders before the (potentially slow)
			// dynamic-import network fetch starts.
			await Promise.resolve();
		}
		try {
			await loader();
		} catch (err) {
			// A lazy route chunk that fails to load is almost always a
			// STALE SPA: a deploy replaced the content-hashed chunk
			// filenames this document was built against, so the URL the
			// loaded bundle asks for no longer exists. (Caddy's SPA
			// fallback even answers it 200 with index.html, so it
			// surfaces as a module parse error, not a clean 404.)
			//
			// Without this branch the rejection escapes into the router's
			// goto(), the route never commits, and — because
			// `_interceptLink` already preventDefault()ed the click — the
			// browser doesn't navigate either. The URL and the nav
			// highlight move, the page does not, and EVERY later nav to
			// an unloaded chunk looks equally dead until the user
			// reloads by hand. That reads as "the side nav stopped
			// working".
			this._recoverFromChunkFailure(err);
			return false;
		} finally {
			if (label) this._chunkLoading = null;
		}
		// A later stale-deploy in this same tab gets its own recovery.
		try { sessionStorage.removeItem(CHUNK_RELOAD_KEY); } catch { /* private mode */ }
		return true;
	};

	// RequireBillingAccess in the backend router now gates /billing/* on a
	// hardcoded named allowlist, not the level-1 staff gate; the acts that
	// freeze the books stay super_admin per route and the pages gate those
	// buttons on isDispatcher. Wraps every /billing/* route's render so a
	// login without billing access never mounts a billing page that would
	// only 403 itself: they see the honest not-found instead, same as an
	// invalid record id.
	// (From 2 September 2026 to 15 September 2026 this gated on
	// isDispatcher, the QuickBooks parallel-month blanket; Paul lifted it.
	// From then until 21 September 2026 it gated on level 1, the set
	// billingGate() admitted; RequireBillingAccess replaced that with a
	// named list served as can_access_billing.)
	private _billingRender = (
		render: (params: { [key: string]: string | undefined }) => unknown,
	) => (params: { [key: string]: string | undefined }): unknown => {
		if (!canAccessBilling(this._user)) return this._shell(html`<cos-not-found></cos-not-found>`);
		return render(params);
	};

	// One reload attempt per target. The URL is already the destination
	// (pushState runs before the router's goto), so reloading lands on
	// the page the user actually clicked, served by a fresh index.html
	// with the current chunk hashes.
	private _recoverFromChunkFailure(err: unknown): void {
		const href = window.location.pathname + window.location.search;
		let lastTried = '';
		try { lastTried = sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? ''; } catch { /* private mode */ }
		if (decideChunkRecovery(lastTried, href) === 'report') {
			// The FRESH document couldn't load it either, so this isn't a
			// stale deploy — offline, or a genuinely broken build. Say so
			// instead of reload-looping; the current page still works.
			try { sessionStorage.removeItem(CHUNK_RELOAD_KEY); } catch { /* private mode */ }
			// console.error is the durable signal — showToast itself
			// dynamic-imports cos-toast, which may be equally unreachable.
			console.error('cos: route chunk failed to load twice', err);
			// The route won't commit, so on a DIRECT load of a broken URL
			// the router has nothing to render — without this the user
			// gets a white screen (worse than the bug this fixes). On an
			// in-app nav the previous page is still mounted, but the
			// error state is the honest thing to show either way.
			this._chunkError = true;
			return;
		}
		try { sessionStorage.setItem(CHUNK_RELOAD_KEY, href); } catch { /* private mode */ }
		window.location.reload();
	}

	// Stage 41b-2.1 — small overlay shown while a lazy route's
	// chunk fetches. The router itself holds rendering until enter
	// resolves, so without this the URL changes but the page sits
	// still for 1–2s on first navigation to /kb/new or /kb/:slug/edit.
	// Empty string = no overlay.
	@state() private _chunkLoading: string | null = null;

	// Set when a route chunk failed twice (see _recoverFromChunkFailure).
	// Cleared by any successful navigation.
	@state() private _chunkError = false;

	// Proactive stale-deploy detection: a new build was deployed while
	// this tab was open (the entry-chunk hash changed). We prompt for a
	// reload BEFORE the user clicks a route whose chunk no longer exists
	// (which the reactive _lazyEnter recovery would otherwise catch, less
	// gracefully). Never auto-reloads — that could drop unsaved work.
	@state() private _updateAvailable = false;
	// The build the user dismissed the prompt for — NOT a plain boolean.
	// A boolean meant one dismissal silenced every later deploy for the life
	// of the tab: polling stopped as soon as an update was known, so a user
	// who closed the banner once in the morning was never told again, and
	// spent the day on stale code being told things were fixed. Remembering
	// WHICH build was waved away lets the next one speak up.
	@state() private _dismissedHash: string | null = loadDismissedHash();
	private _bootHash: string | null = null;
	private _latestHash: string | null = null;
	private _versionInFlight = false;
	private _lastVersionCheck = 0;
	private _versionTimer: number | null = null;

	// Responsive Workstream B — the shell-level tier source for route
	// gating. A shared singleton under the hood (ViewportController.ts);
	// this is the one instance the router guard reads.
	private _viewport = new ViewportController(this);

	// Dedupes the gate-hit telemetry beacon (deliverable 5) so it fires
	// once per (route, tier, level) the user actually LANDS on, not once
	// per re-render — `_shell` recomputes on every render (toasts, chat
	// unread counts, nav customization, …), and none of those are a new
	// "gate hit".
	private _lastGateReported: string | null = null;

	private _reportGateHit(path: string, tier: string, level: CapabilityLevel): void {
		const key = `${path}|${tier}|${level}`;
		if (key === this._lastGateReported) return;
		this._lastGateReported = key;
		void api.reportGateHit(path, tier, level).catch(() => {
			/* best-effort telemetry — a failure here must never surface */
		});
	}

	// The router guard for Responsive Workstream B's capability gates
	// (roam-responsive-pwa-spec.md §7). `_shell` is what every route's
	// `render()` calls, and it already closes over the CURRENT path
	// (`this._path`) and tier (`this._viewport.tier`) — so gating lives
	// here once, with zero changes to the ~100 individual route
	// definitions below.
	//
	//   - `blocked`   → <cos-needs-desktop variant="block"> renders
	//     INSTEAD of the page (the gated component is never mounted —
	//     no wasted fetch, matches the spec's "renders one standard
	//     panel for blocked").
	//   - `degraded` / `view-only` → the page still renders; a slim
	//     banner renders above it, and `gate-level` reflects onto
	//     <cos-app-shell> as an attribute a descendant page CAN consult
	//     (`this.closest('cos-app-shell')?.getAttribute('gate-level')`)
	//     if it wants to adapt further — most don't need to.
	//   - `full` (the overwhelming common case, including every
	//     unlisted route) → unchanged: no banner, no attribute, no
	//     extra render work.
	// A ticket page is drawn only once the user record is known. Until then nobody is
	// assumed to be staff: a level defaulted to 1 would give a library user the staff
	// page (and the staff form) for the moment before /auth/me answers.
	private _whenUserKnown(render: () => unknown): unknown {
		return this._user ? render() : nothing;
	}

	private _shell = (inner: unknown) => {
		const cap = capabilityFor(this._path, this._viewport.tier);
		if (cap.level !== 'full') this._reportGateHit(this._path, this._viewport.tier, cap.level);

		// The library gate (Paul, 15 September 2026: "you should not be able
		// to open anything until you have chosen a library"): the same choke
		// point as the capability gate just above, for the same reason. Every
		// route's render() already funnels through `_shell`, so the check
		// lives here once rather than at each of the ~100 route definitions
		// below. `inner` (the page's own html`<cos-foo-page>` template) is
		// simply left out of `body` when blocked, exactly like the `blocked`
		// capability branch above: the element is never constructed, so its
		// connectedCallback never fires and never fetches. cos-app-shell's
		// own _awaitingClient() makes the identical check (see
		// navigation/library-gate.ts) and renders the "Choose a library"
		// prompt into the resulting empty <slot>. Reads `this._legacyClientId`
		// (the reactive mirror, above), never a bare legacyClientId() call, so
		// this recomputes when a library is chosen or swapped, not only on
		// the next navigation.
		// Digital Archives is open to a library's administrators only
		// (Preserve decision 2, 1 November 2026); a restricted level 4 login
		// who types any /preserve address sees one plain sentence, and the
		// page is never constructed, so nothing is fetched and no error
		// stream follows. The server refuses them as well.
		const preserveAdminsOnly = isPreserveAdminsOnlyPath(this._path) && (this._user?.level ?? 1) >= 4;
		const gateBlocked = libraryGateBlocks(this._path, window.location.search, this._user?.level ?? 1, this._legacyClientId);

		// keyed() by the chosen library: the ONE place every partner-side
		// page gets a clean remount (fresh connectedCallback, fresh fetch)
		// on a library pick or swap, rather than trusting each page to wire
		// its own LEGACY_CLIENT_EVENT listener (cos-thrive-shell's own
		// `_clientEpoch` + keyed() is the same shape, one level down, and a
		// per-page copy of this is exactly the contract a future page would
		// forget). Without this, going from no-library to a page (same
		// `inner` identity, previously `nothing`) still mounts correctly
		// either way, but swapping FROM one already-chosen library to a
		// DIFFERENT one would leave the old library's rows on screen: same
		// tag, same template position, so Lit would only update properties,
		// never re-run connectedCallback.
		//
		// Keyed on a CONSTANT for a non-partner-side path (Communico-only
		// pages, and library staff's own tenant pages, never read the
		// picker): the picker lives in the shell's persistent nav chrome,
		// not gated per page, so a Communico staffer could pick or swap a
		// library while sitting on, say, /mail, and that page must not remount
		// (and lose whatever it was doing) over a choice it never reads.
		const partnerScoped = isPartnerSidePath(this._path, window.location.search);
		// The library's word joins the key so a swap to a library with a
		// different word redraws the page's module-level copy too.
		const remountKey =
			(partnerScoped ? `${this._legacyClientId}:${this._cardholderPlural}` : 'not-partner-scoped') + `:${this._dateOrder}`;
		const body =
			cap.level === 'blocked'
				? html`<cos-needs-desktop
						variant="block"
						surface-label=${cap.label ?? 'This screen'}
						device-label=${deviceHandoffLabel(cap.pattern ?? this._path)}
						note=${cap.note ?? ''}
					></cos-needs-desktop>`
				: html`${cap.level !== 'full'
						? html`<cos-needs-desktop
								variant="banner"
								level=${cap.level}
								surface-label=${cap.label ?? 'This screen'}
								device-label=${deviceHandoffLabel(cap.pattern ?? this._path)}
								note=${cap.note ?? ''}
							></cos-needs-desktop>`
						: ''}${gateBlocked ? nothing : preserveAdminsOnly ? renderAdminsOnly() : keyed(remountKey, inner)}`;

		return html`
			<cos-app-shell .path=${this._path} gate-level=${cap.level} .userEmail=${this._user?.email ?? ''}
				.userName=${this._user ? `${this._user.first_name ?? ''} ${this._user.last_name ?? ''}`.trim() : ''}
				.userId=${this._user?.id ?? 0}
				.userLevel=${this._user?.level ?? 1}
				.userRole=${this._user?.role ?? ''}
				.canAccessBilling=${canAccessBilling(this._user)}
				.userPartnerId=${this._user?.partner_id ?? null}
				.cardholderPlural=${this._cardholderPlural}
				.pendingReportFocus=${this._pendingReportFocus}>
				${body}
			</cos-app-shell>
		`;
	};

	// The `/reserve/calendar` grid's detail card fires `cos:reserve-open-booking`
	// (see the doc comment on cos-reserve-calendar.ts) rather than deciding
	// where a booking's full record lives. This opens it in the shared side
	// panel via `cos-reserve-booking-form` — the same edit surface
	// `cos-reserve-page.ts` already uses — and refreshes the grid on save so
	// the edit is visible without a reload.
	private _openReserveBookingPanel = (e: CustomEvent<{ id: number }>): void => {
		const calendar = e.currentTarget as (HTMLElement & { refresh?: () => Promise<void> }) | null;
		const bookingId = e.detail.id;
		const clientId = libraryClientId() || undefined;
		sidePanelManager.open({
			title: 'Edit booking',
			width: 'lg',
			scrim: false,
			contextKey: 'reserve-booking',
			renderBody: () => html`<cos-reserve-booking-form
				mode="edit"
				.bookingId=${bookingId}
				.clientId=${clientId}
				@saved=${() => {
					sidePanelManager.close();
					void calendar?.refresh?.();
				}}
				@close=${() => sidePanelManager.close()}
			></cos-reserve-booking-form>`,
		});
	};

	/**
	 * B7 (Columbus Reserve Wave 1) — `cos-reserve-calendar`'s detail card
	 * "Duplicate" action. The original booking is untouched; this opens a
	 * BRAND NEW create panel seeded with its room/time/layout and WHO
	 * details, the same `{selection, prefill}` shape
	 * `cos-reserve-booking-form`'s own cancel+rebook flow already carries.
	 * `rooms` is left at its default `[]` — the form loads the pre-selected
	 * room's own details itself (`_loadRoom`) and falls back to that when
	 * building its room dropdown, the same race-tolerant path it already
	 * uses for a grid click or "Book now" (see `_roomOptions()`'s own doc
	 * comment).
	 */
	private _openReserveDuplicatePanel = (e: CustomEvent<{ selection: RoomSelection; prefill: ReserveRebookPrefill }>): void => {
		const calendar = e.currentTarget as (HTMLElement & { refresh?: () => Promise<void> }) | null;
		const clientId = libraryClientId() || undefined;
		sidePanelManager.open({
			title: 'New booking',
			width: 'lg',
			scrim: false,
			contextKey: 'reserve-booking',
			renderBody: () => html`<cos-reserve-booking-form
				mode="create"
				.selection=${e.detail.selection}
				.prefill=${e.detail.prefill}
				.clientId=${clientId}
				@saved=${() => {
					sidePanelManager.close();
					void calendar?.refresh?.();
				}}
				@close=${() => sidePanelManager.close()}
			></cos-reserve-booking-form>`,
		});
	};

	private _router = new Router(this, [
		{
			path: '/',
			enter: async () => {
				if (!(await this._ensureSession())) {
					queueMicrotask(() => this._redirect('/login'));
					return false;
				}
				// Resolve the home page from the user's landing preference —
				// staff get inbox/dashboard/action-center, partner-tier
				// (level >= 3) gets dashboard/action-center. Await the user
				// load so a cold "/" doesn't default-bounce the wrong way.
				queueMicrotask(async () => {
					if (!this._user && this._userPromise) await this._userPromise;
					this._redirect(this._landingPath());
				});
				return false;
			},
			render: () => html``,
		},
		{ path: '/login',         render: () => html`<cos-login></cos-login>` },
		// Reserve Panels — the device app (pairing + room display). PUBLIC,
		// bare, no shell, no auth guard: this is a physical tablet/kiosk, not
		// a staff session — the same shape as /login and
		// /reports/download/:token above. `?fixture=1` (+`&mode=`) runs it
		// against an in-memory fixture backend rather than the real
		// `/api/v1/reserve/panels/*` API (built in parallel by a separate
		// workstream) — see frontend/src/panels/fixtures.ts.
		{ path: '/panels',        enter: this._lazyEnter(async () => true, () => import('./panels/cos-panels-app.js')), render: () => html`<cos-panels-app></cos-panels-app>` },
		{ path: '/inbox',         enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-inbox-page.js')), render: () => this._shell(html`<cos-inbox-page first-name=${this._user?.first_name ?? ''}></cos-inbox-page>`) },
		// Stage 75 — the full notification list (the bell's "View all" target).
		{ path: '/notifications', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-notifications-page.js')), render: () => this._shell(html`<cos-notifications-page></cos-notifications-page>`) },
		{ path: '/dashboard',     enter: this._lazyEnter(this._authGuard, () => import('./components/cos-dashboard-detail.js')), render: () => this._shell(html`<cos-dashboard-detail .userLevel=${this._user?.level ?? 1}></cos-dashboard-detail>`) },
		// Stage 85a — settings (Appearance / Quiet theme). All authed users.
		{ path: '/settings',      enter: this._lazyEnter(this._authGuard, () => import('./components/cos-settings-page.js')), render: () => this._shell(html`<cos-settings-page .userLevel=${this._user?.level ?? 1}></cos-settings-page>`) },
		// Stage 48 — dashboards + reports. /dashboards/:id opens a specific
		// dashboard; /reports is the manager; /activities is the
		// activity-report drill-through target (thin filterable list).
		{ path: '/dashboards/:id', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-dashboard-detail.js')), render: ({ id }) => this._shell(html`<cos-dashboard-detail .dashboardId=${Number(id)}></cos-dashboard-detail>`) },
		{ path: '/reports', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-dashboards-manager.js')), render: () => this._shell(html`<cos-dashboards-manager .currentUserId=${this._user?.id ?? 0}></cos-dashboards-manager>`) },
		// The Reports (data-extract) builder — port of the legacy control-panel
		// "Reports" system (pick fields from a type, filter/sort, export a
		// file). Deliberately a DIFFERENT route from /reports above, which is
		// Stage 48's canned-dashboard-reports registry — same English word,
		// unrelated feature; see the stage report for the naming note.
		// /report-builder/new registered before /report-builder/:id per the
		// house convention (/projects/new before /projects/:id).
		{ path: '/report-builder', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-reports-manager.js')), render: () => this._shell(html`<cos-reports-manager></cos-reports-manager>`) },
		{ path: '/report-builder/new', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-report-builder.js')), render: () => this._shell(html`<cos-report-builder .reportId=${0}></cos-report-builder>`) },
		{ path: '/report-builder/:id', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-report-builder.js')), render: ({ id }) => this._shell(html`<cos-report-builder .reportId=${Number(id)}></cos-report-builder>`) },
		// The landing for the link in a scheduled report's email. It lives
		// in the SPA rather than the API because COS's session is a
		// sessionStorage bearer, not a cookie — a click arriving from an
		// inbox carries nothing a server could read, so only the app can
		// tell "not signed in" from "signed in, wrong library". The auth
		// guard already captures the path and bounces to /login, so an
		// unauthenticated recipient signs in and lands back here with the
		// click intact — that IS the "link signs you in" behaviour.
		// PUBLIC, and rendered bare — no guard, no shell.
		//
		// A production audit of every live report schedule found 35.8% of
		// recipient addresses belong to somebody who can never have a
		// login: shared branch inboxes, contractors, personal webmail.
		// Behind the auth guard this page would bounce every one of them
		// to /login and strand them there. The page decides for itself
		// whether the link it is holding needs a session, and captures a
		// return path when it does.
		{ path: '/reports/download/:token', enter: this._lazyEnter(async () => true, () => import('./components/reports/cos-report-download.js')), render: ({ token }) => html`<cos-report-download .token=${String(token ?? '')}></cos-report-download>` },
		// Thrive's public share link (build-contract-thrive-w1.md deliverable
		// 5) - no login, no shell, same shape as /reports/download/:token
		// above: a bare public route whose page carries no session.
		{ path: '/s/:token', enter: this._lazyEnter(async () => true, () => import('./components/cos-thrive-share-page.js')), render: ({ token }) => html`<cos-thrive-share-page .token=${String(token ?? '')}></cos-thrive-share-page>` },
		// docs/broadcast-book-carousel-spec.md §4 — the anonymous signage
		// page embedded in a Broadcast `html`-type widget iframe (spec §2).
		// No auth guard (an unattended lobby TV has no session) and no
		// _shell() (no nav, no chrome — the iframe is the whole viewport).
		// Tenant comes from the URL PATH, not a header: an iframe can't set
		// one (spec §4). Mirrors /reports/download/:token's public-route
		// shape (the other no-auth, no-shell route in this file).
		{
			path: '/broadcast/carousel/:client/:configId',
			enter: this._lazyEnter(async () => true, () => import('./components/cos-broadcast-carousel-public.js')),
			render: ({ client, configId }) =>
				html`<cos-broadcast-carousel-public .client=${String(client ?? '')} .configId=${String(configId ?? '')}></cos-broadcast-carousel-public>`,
		},
		// Stage 92c — full-window Files (Drive browser). Staff-only (the
		// backend is RequireLevel(1)); reads its own ?drive_id/folder URL.
		{ path: '/files', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-files-page.js')), render: () => this._shell(html`<cos-files-page></cos-files-page>`) },
		// Circulate ILS staff surface (the workspace suite synced from the
		// circulate repo). Entitlement is decided server-side per request via
		// /circulate/context (level-3 partner staff can qualify, so authGuard
		// not staffOnlyGuard); the page reads its own ?m= module param.
		{ path: '/circulate', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-circulate-page.js')), render: () => this._shell(html`<cos-circulate-page .userLevel=${this._user?.level ?? 1}></cos-circulate-page>`) },
		// Lend (Library of Things) standalone surface. Redirects to
		// /circulate?m=lend when the account also has Circulate; the page
		// gates on the lend product via /circulate/context.
		// Reserve — room booking. The reserve IA consolidation (2026-07-31)
		// gives it four destinations under one tab strip + one shared
		// context bar (cos-reserve-tabs / cos-reserve-context-bar /
		// reserve/context.ts): List · Calendar · Approvals · Rooms.
		// List is the front door — it's what a bare /reserve shows — with
		// /reserve/bookings kept as a second, equivalent URL (a bookmark
		// or an existing deep link must keep resolving to the same page).
		// Approvals rides ?view=approvals on this SAME route (it's a view
		// of the List page, not a sibling surface) rather than a path of
		// its own.
		{ path: '/reserve', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-reserve-bookings-list.js')), render: () => this._shell(html`<cos-reserve-bookings-list></cos-reserve-bookings-list>`) },
		{ path: '/reserve/bookings', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-reserve-bookings-list.js')), render: () => this._shell(html`<cos-reserve-bookings-list></cos-reserve-bookings-list>`) },
		// The calendar over the same bookings data — Month/Week/Day. The
		// component is deliberately pure — data in via .fetchBookings, so its
		// tests need no network and Attend could embed it later. The host wires it.
		{ path: '/reserve/calendar', enter: this._lazyEnter(this._authGuard, () => Promise.all([import('./components/cos-reserve-calendar.js'), import('./components/cos-reserve-booking-form.js')])), render: () => this._shell(html`<cos-reserve-calendar .fetchBookings=${(f: ReserveBookingFilters) => api.getReserveBookings({ ...f })} @cos:reserve-open-booking=${this._openReserveBookingPanel} @cos:reserve-duplicate-booking=${this._openReserveDuplicatePanel}></cos-reserve-calendar>`) },
		// Rooms — the room-by-time grid you BOOK from (as distinct from
		// Calendar's grid you READ). Moved off the bare /reserve path so
		// List could take the front door; the grid itself lives in
		// cos-room-picker so Attend's event editor can use it too.
		{ path: '/reserve/rooms', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-reserve-page.js')), render: () => this._shell(html`<cos-reserve-page></cos-reserve-page>`) },
		{ path: '/schedule', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-schedule-page.js')), render: () => this._shell(html`<cos-schedule-page .userLevel=${this._user?.level ?? 1}></cos-schedule-page>`) },
		{ path: '/schedule/new', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-schedule-booking-form.js')), render: () => this._shell(html`<cos-schedule-booking-form .userLevel=${this._user?.level ?? 1}></cos-schedule-booking-form>`) },
		{ path: '/schedule/bookings/:id/edit', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-schedule-booking-form.js')), render: ({ id }) => this._shell(html`<cos-schedule-booking-form .bookingId=${Number(id)} .userLevel=${this._user?.level ?? 1}></cos-schedule-booking-form>`) },
		// Stage 4, slice C (providers): the Providers tab of a PROVIDER category and one person's week.
		{ path: '/schedule/settings/categories/:id/providers', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-schedule-providers-page.js')), render: ({ id }) => this._shell(html`<cos-schedule-providers-page .categoryId=${Number(id)} .userLevel=${this._user?.level ?? 1}></cos-schedule-providers-page>`) },
		{ path: '/schedule/settings/providers/:id/week', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-schedule-provider-week-page.js')), render: ({ id }) => this._shell(html`<cos-schedule-provider-week-page .providerId=${Number(id)} .userLevel=${this._user?.level ?? 1}></cos-schedule-provider-week-page>`) },
		// Stage 4, slice B: the Group editor. `new` before `:id`, so the static
		// segment is not read as a group id.
		{ path: '/schedule/settings/groups/new', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-schedule-group-editor.js')), render: () => this._shell(html`<cos-schedule-group-editor .userLevel=${this._user?.level ?? 1}></cos-schedule-group-editor>`) },
		{ path: '/schedule/settings/groups/:id', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-schedule-group-editor.js')), render: ({ id }) => this._shell(html`<cos-schedule-group-editor .groupId=${Number(id)} .userLevel=${this._user?.level ?? 1}></cos-schedule-group-editor>`) },
		{ path: '/schedule/bookings/:id', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-schedule-booking-page.js')), render: ({ id }) => this._shell(html`<cos-schedule-booking-page .bookingId=${Number(id)} .userLevel=${this._user?.level ?? 1}></cos-schedule-booking-page>`) },
		// Stage 4, slice A (categories): the settings list, "New category" (the
		// list with its dialog open) and the category editor.
		{ path: '/schedule/settings', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-schedule-settings.js')), render: () => this._shell(html`<cos-schedule-settings .userLevel=${this._user?.level ?? 1}></cos-schedule-settings>`) },
		{ path: '/schedule/settings/categories/new', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-schedule-settings.js')), render: () => this._shell(html`<cos-schedule-settings .openNew=${true} .userLevel=${this._user?.level ?? 1}></cos-schedule-settings>`) },
		{ path: '/schedule/settings/categories/:id', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-schedule-category-editor.js')), render: ({ id }) => this._shell(html`<cos-schedule-category-editor .categoryId=${Number(id)} .userLevel=${this._user?.level ?? 1}></cos-schedule-category-editor>`) },
		{ path: '/lend', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-lend-page.js')), render: () => this._shell(html`<cos-lend-page></cos-lend-page>`) },
		// Preserve (digital archives) staff surface — authGuard like the
		// other partner products; the page gates on the preserve product
		// via /circulate/context.
		{ path: '/media', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-media-page.js')), render: () => this._shell(html`<cos-media-page></cos-media-page>`) },
		{ path: '/preserve', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-preserve-page.js')), render: () => this._shell(html`<cos-preserve-page .userLevel=${this._user?.level ?? 1}></cos-preserve-page>`) },
		// Item detail (fleet/preserve-detail-nav) — its own routed page, not
		// an inline panel above the items table. Registered BEFORE
		// /preserve/items/:id so "new" is never parsed as an id.
		{ path: '/preserve/items/new', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-preserve-item-detail.js')), render: () => this._shell(html`<cos-preserve-item-detail .itemId=${0} .userLevel=${this._user?.level ?? 1}></cos-preserve-item-detail>`) },
		{ path: '/preserve/items/:id', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-preserve-item-detail.js')), render: ({ id }) => this._shell(html`<cos-preserve-item-detail .itemId=${Number(id)} .userLevel=${this._user?.level ?? 1}></cos-preserve-item-detail>`) },
		// Contribution review detail — same navigation model as the item
		// detail page above.
		// Oral history review (transcripts, vocabulary tally). Literal
		// segments after the id, so they never collide with the bare
		// /preserve/items/:id record page above.
		{ path: '/preserve/items/:id/recording', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-preserve-oral-recording.js')), render: ({ id }) => this._shell(html`<cos-preserve-oral-recording .itemId=${Number(id)} .userLevel=${this._user?.level ?? 1}></cos-preserve-oral-recording>`) },
		{ path: '/preserve/oral-history/:id', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-preserve-oral-collection.js')), render: ({ id }) => this._shell(html`<cos-preserve-oral-collection .collectionId=${Number(id)} .userLevel=${this._user?.level ?? 1}></cos-preserve-oral-collection>`) },
		{ path: '/preserve/contributions/:id', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-preserve-contribution-detail.js')), render: ({ id }) => this._shell(html`<cos-preserve-contribution-detail .contributionId=${Number(id)} .userLevel=${this._user?.level ?? 1}></cos-preserve-contribution-detail>`) },
		// P3 entity hub + unlinked-items worklist ("the Orphanarium") -- same
		// routed-page navigation model as above.
		{ path: '/preserve/entities/:tagType/:tagText', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-preserve-entity-hub.js')), render: ({ tagType, tagText }) => this._shell(html`<cos-preserve-entity-hub .tagType=${String(tagType)} .tagText=${String(tagText)} .userLevel=${this._user?.level ?? 1}></cos-preserve-entity-hub>`) },
		{ path: '/preserve/unlinked', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-preserve-unlinked.js')), render: () => this._shell(html`<cos-preserve-unlinked .userLevel=${this._user?.level ?? 1}></cos-preserve-unlinked>`) },
		// Roster (staff rostering) staff surface — same gate model as
		// Preserve: authGuard here, product entitlement in the page.
		{ path: '/roster', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-roster-page.js')), render: () => this._shell(html`<cos-roster-page .userLevel=${this._user?.level ?? 1}></cos-roster-page>`) },
		// Roster Stage 9 — the tap-to-clock confirm card
		// (docs/stage-briefs/roster-timeclock-brief.md). PUBLIC, bare, no
		// shell — an NFC tap or a printed QR scan opens this in a BRAND
		// NEW browser tab with no COS session in it (sessionStorage is
		// tab-scoped by design), so it can't sit behind the normal
		// _authGuard the way every other authenticated route does: that
		// guard's redirect-to-/login runs BEFORE the matched route ever
		// renders, with nothing for the post-login flow to come back TO
		// unless the exact URL it captures is this one. cos-roster-clock
		// runs that same restoreSession()-then-capture-and-redirect flow
		// itself instead (see its file header) — same shape as /panels
		// and /reports/download/:token above.
		{ path: '/clock/:token', enter: this._lazyEnter(async () => true, () => import('./components/cos-roster-clock.js')), render: ({ token }) => html`<cos-roster-clock .token=${String(token ?? '')}></cos-roster-clock>` },
		// Outreach: ONE rail entry, two sections (Paul, 2026-08-23; see
		// docs/outreach-spec.md's amended §1.3 note). Same gate model as
		// Roster throughout: authGuard here, product entitlement in the
		// page.
		//
		// Visits: cos-outreach-visits.ts / cos-outreach-visit-detail.ts
		// are owned by a parallel branch and do not exist on this branch
		// yet; the build fails resolving these two dynamic imports until
		// that branch merges. That is expected; see the stage report.
		{ path: '/outreach/visits', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-outreach-visits.js')), render: () => this._shell(html`<cos-outreach-visits></cos-outreach-visits>`) },
		{ path: '/outreach/visits/:id', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-outreach-visit-detail.js')), render: ({ id }) => this._shell(html`<cos-outreach-visit-detail .visitId=${Number(id)}></cos-outreach-visit-detail>`) },
		// Bookmobile: re-parented from /bookmobile* (Stage 1 only:
		// vehicles, stops, routes; no schedule/trip route exists,
		// docs/outreach-spec.md §6). The three page components are
		// unchanged; only their URL and their nav.ts tab data moved.
		{ path: '/outreach/bookmobile', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-bookmobile-vehicles.js')), render: () => this._shell(html`<cos-bookmobile-vehicles></cos-bookmobile-vehicles>`) },
		{ path: '/outreach/bookmobile/stops', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-bookmobile-stops.js')), render: () => this._shell(html`<cos-bookmobile-stops></cos-bookmobile-stops>`) },
		{ path: '/outreach/bookmobile/routes', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-bookmobile-routes.js')), render: () => this._shell(html`<cos-bookmobile-routes></cos-bookmobile-routes>`) },
		{ path: '/outreach/bookmobile/trips', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-outreach-trips.js')), render: () => this._shell(html`<cos-outreach-trips></cos-outreach-trips>`) },
		{ path: '/outreach/bookmobile/public-timetable', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-outreach-public-timetable.js')), render: () => this._shell(html`<cos-outreach-public-timetable></cos-outreach-public-timetable>`) },
		// Two addresses a worker is likely to type or bookmark: the bare
		// /outreach opens Visits, and /outreach/bookmobile/vehicles opens
		// Vehicles (the Bookmobile fleet page, whose canonical path is
		// /outreach/bookmobile). Both redirect rather than show "Page not
		// found".
		{ path: '/outreach', enter: this._authGuard, render: () => { queueMicrotask(() => this._navigate('/outreach/visits')); return html``; } },
		{ path: '/outreach/bookmobile/vehicles', enter: this._authGuard, render: () => { queueMicrotask(() => this._navigate('/outreach/bookmobile')); return html``; } },
		// The old /bookmobile* URLs redirect rather than 404: bookmarks
		// and muscle memory land on the same page (the /implementation/new
		// → /projects/new precedent). Registered AFTER the /outreach/*
		// routes above so nothing shadows them; these three are disjoint
		// literal paths so registration order among themselves doesn't
		// matter.
		{ path: '/bookmobile', enter: this._authGuard, render: () => { queueMicrotask(() => this._redirect('/outreach/bookmobile')); return html``; } },
		{ path: '/bookmobile/stops', enter: this._authGuard, render: () => { queueMicrotask(() => this._redirect('/outreach/bookmobile/stops')); return html``; } },
		{ path: '/bookmobile/routes', enter: this._authGuard, render: () => { queueMicrotask(() => this._redirect('/outreach/bookmobile/routes')); return html``; } },
		// Measure (analytics platform) staff surface — same gate model.
		// The corkscrew (MEASURE-ROLE-DEPTH-DESIGN.md): /measure is the Today
		// (L0) pulse; the Stage-1 validation dashboard moved to /measure/pipeline
		// (component unchanged, route + a header link only); /measure/d/*
		// is the drill shell, which resolves its OWN tail via the shared
		// registry (frontend/src/measure/registry.ts) — a single wildcard
		// route covers every depth, so a new registered level never needs a
		// new route here.
		{ path: '/measure', enter: this._authGuard, render: () => { queueMicrotask(() => this._redirect(thriveRedirectFor({ kind: 'overview' }, window.location.search))); return html``; } },
		{ path: '/measure/pipeline', enter: this._authGuard, render: () => { queueMicrotask(() => this._redirect(thriveRedirectFor({ kind: 'pipeline' }, window.location.search))); return html``; } },
		{ path: '/measure/d', enter: this._authGuard, render: () => { queueMicrotask(() => this._redirect(thriveRedirectFor({ kind: 'drill', tail: '' }, window.location.search))); return html``; } },
		{ path: '/measure/d/*', enter: this._authGuard, render: (params) => { queueMicrotask(() => this._redirect(thriveRedirectFor({ kind: 'drill', tail: String(params[0] ?? '') }, window.location.search))); return html``; } },
		// Thrive (build-contract-thrive-w1.md) - one wildcard route, same
		// shape as the retired /measure/d/*: cos-thrive-shell resolves its
		// own tail via thrive/registry.ts, so a new registered page never
		// needs a new route here. /thrive alone (no tail) is Overview.
		{ path: '/thrive', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-thrive-shell.js')), render: () => this._shell(html`<cos-thrive-shell path="" .userLevel=${this._user?.level ?? 1}></cos-thrive-shell>`) },
		{ path: '/thrive/*', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-thrive-shell.js')), render: (params) => this._shell(html`<cos-thrive-shell path=${String(params[0] ?? '')} .userLevel=${this._user?.level ?? 1}></cos-thrive-shell>`) },
		// Community — the embedded cross-library professional network (feed,
		// spaces, DMs, events, jobs, product updates + the Communico-staff
		// admin + AI staff dashboard). _authGuard so level-3 library staff
		// reach it too; the community service's OWN role model gates its
		// admin/staff surfaces server-side. The whole SPA loads as one lazy
		// chunk; enablement is per-deployment via /community/context.
		{ path: '/community', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-community-page.js')), render: () => this._shell(html`<cos-community-page></cos-community-page>`) },
		// Communications Hub — EVERY library gets this, whatever else they
		// bought (the Hub is the platform's delivery rail, not a purchased
		// module), so there is no product entitlement gate here.
		{ path: '/communications', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-comms-page.js')), render: () => this._shell(html`<cos-comms-page .userLevel=${this._user?.level ?? 1}></cos-comms-page>`) },
		// Desk (docs/desk-plan.md) — the front-of-house staff surface: scan
		// or search, get the person, do the thing. Ungated like
		// /communications above (a desk is not a purchased product — its
		// task lanes derive their own entitlement from /circulate/context);
		// backend reads sit at RequireLevelScoped(3), so _authGuard is the
		// right client-side gate.
		{ path: '/desk', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-desk-page.js')), render: () => this._shell(html`<cos-desk-page .userLevel=${this._user?.level ?? 1}></cos-desk-page>`) },
		// Action Center (docs/action-center-plan.md) — the merged work queue.
		// RequireLevelScoped(3) server-side, so _authGuard (any authenticated
		// user) is the right client-side gate, same as /communications above.
		{ path: '/action-center', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-action-center.js')), render: () => this._shell(html`<cos-action-center .userLevel=${this._user?.level ?? 1}></cos-action-center>`) },
		// Billing — the Maxio/SaaSOptics replacement. Staff-only server-side
		// (RequireLevel(1) on every /billing route); the pages gate their write
		// affordances on userLevel so a partner-tier viewer never sees a
		// control that would 403.
		//
		// THE BILLING HOME. /billing used to have no route at all, so the nav
		// landed on the contracts list — a directory sorted by import
		// timestamp. This is the "what needs me this morning" surface.
		{ path: '/billing', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-billing-overview.js')), render: this._billingRender(() => this._shell(html`<cos-billing-overview></cos-billing-overview>`)) },
		// The renewal worklist. One segment after /billing, like /billing/close
		// — /billing/contracts/:id is two, so neither can shadow the other.
		{ path: '/billing/renewals', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-billing-renewals.js')), render: this._billingRender(() => this._shell(html`<cos-billing-renewals></cos-billing-renewals>`)) },
		{ path: '/billing/contracts', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-billing-contracts-list.js')), render: this._billingRender(() => this._shell(html`<cos-billing-contracts-list></cos-billing-contracts-list>`)) },
		// Registered BEFORE /billing/contracts/:id so "new" is not parsed
		// as a contract id.
		{ path: '/billing/contracts/new', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-billing-contract-new.js')), render: this._billingRender(() => this._shell(html`<cos-billing-contract-new .partnerId=${Number(new URLSearchParams(location.search).get('partner_id') ?? 0)} .userLevel=${this._user?.level ?? 1}></cos-billing-contract-new>`)) },
		{ path: '/billing/contracts/:id', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-billing-contracts-detail.js')), render: this._billingRender(({ id }) => this._shell(html`<cos-billing-contracts-detail .contractId=${Number(id)} .userLevel=${this._user?.level ?? 1} .isSuperAdmin=${isDispatcher(this._user)}></cos-billing-contracts-detail>`)) },
		// Brief G — retired. Renew/Extend/End early are now hosted IN PLACE on
		// contract detail (<cos-billing-renewal-panels>, shared with
		// /billing/customers/:id) rather than this divergent full-page form.
		// Redirects rather than deletes the route outright: cos-billing-
		// renewals.ts and cos-billing-overview.ts both still link here (a
		// worklist and the billing home) and neither needs updating — they
		// land on the unified experience automatically. Mirrors the /chat →
		// /conversations redirect above.
		{ path: '/billing/contracts/:id/renew', enter: async ({ id }) => { queueMicrotask(() => this._redirect(`/billing/contracts/${id}?open=renew`)); return false; }, render: () => html`` },
		{ path: '/billing/contracts/:id/amend', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-billing-contract-amend.js')), render: this._billingRender(({ id }) => this._shell(html`<cos-billing-contract-amend .contractId=${Number(id)} .userLevel=${this._user?.level ?? 1}></cos-billing-contract-amend>`)) },
		{ path: '/billing/accounts', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-billing-accounts-list.js')), render: this._billingRender(() => this._shell(html`<cos-billing-accounts-list></cos-billing-accounts-list>`)) },
		{ path: '/billing/accounts/:id', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-billing-accounts-detail.js')), render: this._billingRender(({ id }) => this._shell(html`<cos-billing-accounts-detail .accountId=${Number(id)} .userLevel=${this._user?.level ?? 1} .isSuperAdmin=${isDispatcher(this._user)}></cos-billing-accounts-detail>`)) },
		// The customer index — one row per partner. One segment after
		// /billing, so /billing/customers/:id (two) cannot shadow it.
		{ path: '/billing/customers', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-billing-customers-list.js')), render: this._billingRender(() => this._shell(html`<cos-billing-customers-list></cos-billing-customers-list>`)) },
		// The customer contract page — a partner id. Two segments after
		// /billing, like /billing/accounts/:id, so neither can shadow the
		// other (different first segment).
		{ path: '/billing/customers/:id', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-billing-customer-detail.js')), render: this._billingRender(({ id }) => this._shell(html`<cos-billing-customer-detail .customerId=${Number(id)} .userLevel=${this._user?.level ?? 1} .isSuperAdmin=${isDispatcher(this._user)}></cos-billing-customer-detail>`)) },
		{ path: '/billing/products', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-billing-products.js')), render: this._billingRender(() => this._shell(html`<cos-billing-products .userLevel=${this._user?.level ?? 1}></cos-billing-products>`)) },
		// Order forms, quotes and renewals — one document, three clause sets.
		// One segment after /billing, like /billing/close, so
		// /billing/contracts/:id (two) cannot shadow it.
		{ path: '/billing/documents', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-billing-documents.js')), render: this._billingRender(() => this._shell(html`<cos-billing-documents></cos-billing-documents>`)) },
		// (RequireLevel(1)); the writes that put a document in front of a
		// customer or freeze the books (invoice issue, invoice run, credit
		// note, period close) are RequireSuperAdmin on top, so the pages take
		// isSuperAdmin purely to avoid offering an action that would 403 —
		// the server remains the gate. /billing/close is one segment after
		// /billing, so /billing/invoices/:id (two) cannot shadow it.
		{ path: '/billing/invoices', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-billing-invoices.js')), render: this._billingRender(() => this._shell(html`<cos-billing-invoices .isSuperAdmin=${isDispatcher(this._user)}></cos-billing-invoices>`)) },
		{ path: '/billing/invoices/:id', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-billing-invoice-detail.js')), render: this._billingRender(({ id }) => this._shell(html`<cos-billing-invoice-detail .invoiceId=${Number(id)} .isSuperAdmin=${isDispatcher(this._user)}></cos-billing-invoice-detail>`)) },
		{ path: '/billing/close', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-billing-close.js')), render: this._billingRender(() => this._shell(html`<cos-billing-close .isSuperAdmin=${isDispatcher(this._user)}></cos-billing-close>`)) },
		// Where COS and Maxio disagree, plus the worklist of Maxio
		// customers nobody could match. One segment after /billing, like
		// /billing/close, so /billing/contracts/:id cannot shadow it.
		{ path: '/billing/reconciliation', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-billing-reconciliation.js')), render: this._billingRender(() => this._shell(html`<cos-billing-reconciliation .userLevel=${this._user?.level ?? 1} .isSuperAdmin=${isDispatcher(this._user)}></cos-billing-reconciliation>`)) },
		// Cheque intake (migration 000462). One segment after /billing,
		// like /billing/close, so /billing/invoices/:id (two) cannot
		// shadow it. The open batch rides in ?batch=, which the page
		// reads and pushes itself: the router treats pathname+search as
		// the nav key but will not re-mount on a same-path query change.
		{ path: '/billing/deposits', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-billing-deposits.js')), render: this._billingRender(() => this._shell(html`<cos-billing-deposits></cos-billing-deposits>`)) },
		// Bank activity (migration 000472). One segment after /billing,
		// like /billing/deposits, so /billing/invoices/:id cannot shadow
		// it. The tab and filters ride in the query, which the page reads
		// and pushes itself: the router treats pathname+search as the nav
		// key but will not re-mount on a same-path query change.
		{ path: '/billing/bank-activity', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-billing-bank-activity.js')), render: this._billingRender(() => this._shell(html`<cos-billing-bank-activity></cos-billing-bank-activity>`)) },
		// The editable invoice-send email template (Brief AK). One segment
		// after /billing, like /billing/close.
		{ path: '/billing/invoice-settings', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-billing-invoice-settings.js')), render: this._billingRender(() => this._shell(html`<cos-billing-invoice-settings .isSuperAdmin=${isDispatcher(this._user)}></cos-billing-invoice-settings>`)) },
		// The payment-reminder schedule and the reminder/statement templates
		// are a SECTION of the page above, not a page of their own, this
		// route is the same component opened scrolled to it, so a deep link
		// works without spending a rail slot on it (Brief AO).
		{ path: '/billing/reminder-settings', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-billing-invoice-settings.js')), render: this._billingRender(() => this._shell(html`<cos-billing-invoice-settings focus-section="reminders-section" .isSuperAdmin=${isDispatcher(this._user)}></cos-billing-invoice-settings>`)) },
		// The finance reporting surface (plan §4.7): ARR, MRR, the ARR
		// waterfall (which is the churn report) and cohort retention. Read-only
		// — every figure comes from /billing/metrics/*, so no isSuperAdmin.
		// One segment after /billing, like /billing/close.
		{ path: '/billing/reports', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-billing-reports.js')), render: this._billingRender(() => this._shell(html`<cos-billing-reports></cos-billing-reports>`)) },
		// Nurture (CRM) staff surface — same gate model: authGuard here,
		// product entitlement in the page, staff-only enforced server-side.
		{ path: '/nurture', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-nurture-page.js')), render: () => this._shell(html`<cos-nurture-page></cos-nurture-page>`) },
		// Segments must register BEFORE /nurture/:id — same segment count,
		// and @lit-labs/router matches in array order ("segments" would
		// otherwise parse as a NaN person id).
		{ path: '/nurture/segments', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-nurture-segments.js')), render: () => this._shell(html`<cos-nurture-segments></cos-nurture-segments>`) },
		// Email library (fleet/email-library) — an email is its own asset,
		// in its own library; a single master/detail route like segments.
		// Must register BEFORE /nurture/:id (one segment).
		{ path: '/nurture/emails', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-nurture-emails.js')), render: () => this._shell(html`<cos-nurture-emails></cos-nurture-emails>`) },
		// Email Studio (§10.3) — the structured-document editor for ONE
		// EMAIL ASSET (decoupled from campaigns, fleet/email-library). Three
		// segments after /nurture, so it can't collide with /nurture/:id
		// (one segment) or /nurture/emails (one segment) regardless of order.
		// "New blank email": the editor opens with no row behind it and creates the
		// email on the first edit (see cos-email-studio-page). Literal, so it can never be read as an id.
		{ path: '/nurture/emails/new/studio', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-email-studio-page.js')), render: () => this._shell(html`<cos-email-studio-page .emailId=${0}></cos-email-studio-page>`) },
		{ path: '/nurture/emails/:id/studio', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-email-studio-page.js')), render: ({ id }) => this._shell(html`<cos-email-studio-page .emailId=${Number(id)}></cos-email-studio-page>`) },
		// Campaigns (Converse Stage 1) — a single master/detail route like
		// segments; must also register BEFORE /nurture/:id (one segment).
		{ path: '/nurture/campaigns', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-nurture-campaigns.js')), render: () => this._shell(html`<cos-nurture-campaigns></cos-nurture-campaigns>`) },
		// fleet/email-library — the OLD per-campaign studio route is now a
		// thin redirect: lift the campaign's legacy body_doc (or a blank
		// canvas, absent one) into its own email asset, attach it, then
		// land on the new /nurture/emails/:id/studio route. Idempotent on
		// the backend (a re-visited link is a no-op, not a second asset).
		// A lift failure (e.g. the campaign was archived/deleted meanwhile)
		// falls back to the campaign itself rather than a dead page.
		{
			path: '/nurture/campaigns/:id/studio',
			enter: async ({ id }) => {
				if (!(await this._authGuard())) return false;
				try {
					const email = (await api.liftCampaignEmail(Number(id))) as { id: number };
					queueMicrotask(() => this._redirect(`/nurture/emails/${email.id}/studio`));
				} catch {
					queueMicrotask(() => this._redirect('/nurture/campaigns'));
				}
				return false;
			},
			render: () => html``,
		},
		// Automated Journeys (§10.5) — a third master/detail route, same
		// shape as segments/campaigns; must also register BEFORE /nurture/:id.
		{ path: '/nurture/journeys', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-nurture-journeys.js')), render: () => this._shell(html`<cos-nurture-journeys></cos-nurture-journeys>`) },
		// Surveys (§13, v1 spine) — the list. Promoted out of Marketing's
		// tab set into its own rail entry (Paul, 2026-08-20), split into a
		// routed list + workspace pair (was one combined master/detail
		// page). Must register BEFORE /nurture/:id (same segment count).
		{ path: '/nurture/surveys', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-nurture-surveys.js')), render: () => this._shell(html`<cos-nurture-surveys></cos-nurture-surveys>`) },
		// A fresh, unsaved survey draft — the same workspace component as
		// /nurture/surveys/:sid below, with surveyId=0 as the sentinel for
		// "not yet created" (the /report-builder/new + .reportId=${0} and
		// /preserve/items/new + .itemId=${0} precedent). Must register
		// BEFORE /nurture/surveys/:sid (same segment count, and "new"
		// would otherwise parse as a NaN sid).
		{ path: '/nurture/surveys/new', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-nurture-survey-detail.js')), render: () => this._shell(html`<cos-nurture-survey-detail .surveyId=${0}></cos-nurture-survey-detail>`) },
		// The survey workspace (Build / Responses / Results, via ?tab=) —
		// three segments after /nurture, so it can't collide with
		// /nurture/:id (one segment) or /nurture/surveys (two segments)
		// regardless of route order (the Email Studio precedent).
		{ path: '/nurture/surveys/:sid', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-nurture-survey-detail.js')), render: ({ sid }) => this._shell(html`<cos-nurture-survey-detail .surveyId=${Number(sid)}></cos-nurture-survey-detail>`) },
		// Organisations (nurture-orgs-roles brief) — a fourth master/detail
		// route on the Patrons capability; must also register BEFORE
		// /nurture/:id (same segment count).
		{ path: '/nurture/orgs', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-nurture-orgs.js')), render: () => this._shell(html`<cos-nurture-orgs></cos-nurture-orgs>`) },
		// §9.4 work queue (nurture-work-queue brief) — a fifth master/detail-
		// adjacent route on the Patrons capability; must also register
		// BEFORE /nurture/:id (same segment count).
		{ path: '/nurture/work-queue', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-nurture-work-queue.js')), render: () => this._shell(html`<cos-nurture-work-queue></cos-nurture-work-queue>`) },
		// §9.3 inbound email (nurture-unified-inbox-audit) — a sixth
		// master/detail-adjacent route on the Patrons capability; must also
		// register BEFORE /nurture/:id (same segment count).
		{ path: '/nurture/inbound-email', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-nurture-inbound-email.js')), render: () => this._shell(html`<cos-nurture-inbound-email></cos-nurture-inbound-email>`) },
		// §9.6 the Boardroom (nurture-boardroom brief) — meetings, agendas,
		// minutes, publishing; a sixth master/detail-adjacent route on the
		// Patrons capability; must also register BEFORE /nurture/:id (same
		// segment count).
		{ path: '/nurture/board', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-nurture-board.js')), render: () => this._shell(html`<cos-nurture-board></cos-nurture-board>`) },
		// isSuperAdmin (fleet/email-purge brief) purely to avoid offering an
		// action that would 403 — the backend's own mw.RequireSuperAdmin()
		// route gate is what actually protects the permanent-delete
		// correspondence action.
		{ path: '/nurture/:id', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-nurture-person.js')), render: ({ id }) => this._shell(html`<cos-nurture-person .personId=${Number(id)} .isSuperAdmin=${isDispatcher(this._user)}></cos-nurture-person>`) },
		// Broadcast (digital signage) staff surface — same gate model as
		// Preserve/Roster: authGuard here, product entitlement in the
		// page. Stage B0 shell; docs/broadcast-port-plan.md.
		{ path: '/broadcast', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-broadcast-page.js')), render: () => this._shell(html`<cos-broadcast-page></cos-broadcast-page>`) },
		{ path: '/attend', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-attend-page.js')), render: () => this._shell(html`<cos-attend-page .userLevel=${this._user?.level ?? 1}></cos-attend-page>`) },
		{ path: '/attend/events/new', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-attend-editor.js')), render: () => this._shell(html`<cos-attend-editor .eventId=${0} .userLevel=${this._user?.level ?? 1}></cos-attend-editor>`) },
		{ path: '/attend/events/:id/edit', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-attend-editor.js')), render: ({ id }) => this._shell(html`<cos-attend-editor .eventId=${Number(id)} .userLevel=${this._user?.level ?? 1}></cos-attend-editor>`) },
		// Collections (Explore-carousel manager + CUSTOM type port) — not
		// gated by an entitlement lever like Broadcast/Attend/Reserve: it's
		// a free Explore feature, so the page's own per-client access check
		// (GET /collections/context) is the only gate.
		{ path: '/collections', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-collections-page.js')), render: () => this._shell(html`<cos-collections-page .userLevel=${this._user?.level ?? 1}></cos-collections-page>`) },
		// The library's own door to the Annual Report (the staff Reports tab at
		// /report-builder is level 1 only). Backend is RequireLevelScoped(3).
		{ path: '/annual-report', enter: this._lazyEnter(this._authGuard, () => import('./components/reports/cos-annual-report-page.js')), render: () => this._shell(html`<cos-annual-report-page></cos-annual-report-page>`) },
		// Explore admin. Both the bare landing and /explore/:tab render the
		// same page, which reads the tab from the path itself; an unknown
		// segment falls back to Curation rather than 404ing, so a stale
		// bookmark still lands somewhere useful.
		{ path: '/explore', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-explore-page.js')), render: () => this._shell(html`<cos-explore-page .userLevel=${this._user?.level ?? 1}></cos-explore-page>`) },
		{ path: '/explore/:tab', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-explore-page.js')), render: () => this._shell(html`<cos-explore-page .userLevel=${this._user?.level ?? 1}></cos-explore-page>`) },
		// Partner-workspace Admin: per-product settings (Lend program
		// admin now; Circulate + future modules as they arrive). The page
		// gates on admin role client-side; the lend admin API is
		// guard-gated server-side through the /circulation proxy.
		{ path: '/partner-admin', enter: this._lazyEnter(this._partnerAdminGuard, () => import('./components/cos-partner-admin-page.js')), render: () => this._shell(html`<cos-partner-admin-page .libraryAdmin=${!isAdmin(this._user)} .libraryProducts=${isLinkedLevel2(this._user) ? LIBRARY_LEVEL2_PRODUCTS : LIBRARY_ADMIN_PRODUCTS}></cos-partner-admin-page>`) },
		{ path: '/activities', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-activities-list.js')), render: () => this._shell(html`<cos-activities-list></cos-activities-list>`) },
		// Stage 66 — "My activity": own numbers vs the anonymous role
		// average, open to ALL staff (the locked own-stats decision). Same
		// component as /admin/activity without the Team tab.
		{ path: '/activity', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-admin-activity.js')), render: () => this._shell(html`<cos-admin-activity></cos-admin-activity>`) },
		// Stage 42e — per-staff "My work" surface. Lazy-loaded
		// chunk; gated to staff. Lands at /me/implementations.
		{
			path: '/me/implementations',
			enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-me-implementations.js')),
			render: () => this._shell(html`<cos-me-implementations></cos-me-implementations>`),
		},
		{
			path: '/partners',
			// bundle-entry — cos-partner-list moved to lazy-load. The bespoke
			// guard below (session + level-3 redirect + library-from-URL) is
			// unchanged; it's passed as _lazyEnter's guard arg so the chunk
			// only fetches once the guard actually passes.
			enter: this._lazyEnter(
				async () => {
					if (!(await this._ensureSession())) {
						queueMicrotask(() => { captureRedirectAfterLogin(); if (window.location.pathname !== '/login') this._redirect('/login'); });
						return false;
					}
					if (!this._user && this._userPromise) await this._userPromise;
					// Level-3 users have only one partner — redirect them
					// straight to its detail page rather than showing a
					// single-row list.
					if (this._user && this._user.level !== 1 && this._user.partner_id) {
						queueMicrotask(() => this._redirect(`/partners/${this._user!.partner_id}`));
						return false;
					}
					// /partners has no shared guard to fold this into (it's the one
					// route with its own bespoke enter) — see _applyLibraryFromURL's
					// header comment for why this has to resolve before `enter`
					// returns true.
					await this._applyLibraryFromURL();
					this._captureReportFocusFromURL();
					await this._ensureLibraryChosen();
					return true;
				},
				() => import('./components/cos-partner-list.js'),
			),
			render: () => this._shell(html`<cos-partner-list .userLevel=${this._user?.level ?? 1} .userPartnerId=${this._user?.partner_id ?? null}></cos-partner-list>`)
		},
		// Partner map view (2026-08) — staff-only; MUST register before
		// /partners/:id (the wildcard would otherwise swallow "map" as an
		// id, the same filter-options/record-layout hazard documented
		// throughout this route table).
		{ path: '/partners/map', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-partner-map.js')), render: () => this._shell(html`<cos-partner-map></cos-partner-map>`) },
		// Stage 36b — /partners/:id lazy-loaded per Stage 34c discipline.
		// The component grew to 1000+ lines with the contact_completeness
		// banner + annual-reviews timeline + contact-row inline-expand;
		// keeping it in the entry bundle was net-negative for any user
		// who never navigated into a single partner.
		{ path: '/partners/:id',  enter: this._lazyEnter(this._authGuard, () => import('./components/cos-partner-detail.js')), render: ({ id }) => this._shell(html`<cos-partner-detail .partnerId=${Number(id)} .userLevel=${this._user?.level ?? 1} .canAccessBilling=${canAccessBilling(this._user)}></cos-partner-detail>`) },
		// Stage 60b — Prospects (unlinked PLS libraries). Staff-only; both
		// list + single-record pages lazy-load. /prospects/:fscskey is the
		// v1 "simple" IMLS record view (Stage 60d replaces it).
		{ path: '/prospects', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-prospects-list.js')), render: () => this._shell(html`<cos-prospects-list></cos-prospects-list>`) },
		// Stage 60g — a prospect backed by a partner row (HubSpot-only or
		// IMLS+history) renders the partner-detail layout under a prospect URL +
		// breadcrumb, so the address bar doesn't claim it's a customer. Distinct
		// segment count from /prospects/:fscskey, so route order is immaterial.
		{ path: '/prospects/by-partner/:id', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-prospect-by-partner.js')), render: ({ id }) => this._shell(html`<cos-prospect-by-partner .partnerId=${Number(id)} .userLevel=${this._user?.level ?? 1} .canAccessBilling=${canAccessBilling(this._user)}></cos-prospect-by-partner>`) },
		{ path: '/prospects/:fscskey', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-prospect-record.js')), render: ({ fscskey }) => this._shell(html`<cos-prospect-record .fscskey=${String(fscskey)}></cos-prospect-record>`) },
		// Stage 61b — the /mail personal inbox. Staff-only; lazy-loaded
		// (DOMPurify + the three-pane page stay out of the boot chunk).
		{ path: '/mail', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-mail-inbox.js')), render: () => this._shell(html`<cos-mail-inbox></cos-mail-inbox>`) },
		{ path: '/calendar', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-calendar-page.js')), render: () => this._shell(html`<cos-calendar-page></cos-calendar-page>`) },
		{ path: '/calendar/team', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-calendar-page.js')), render: () => this._shell(html`<cos-calendar-page></cos-calendar-page>`) },
		// Stream 2 REWORK-C — the page route is /conversations; /chat redirects
		// (old bookmarks + notification/inbox deep-links, preserving ?c=/?m=).
		{ path: '/conversations', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-chat.js')), render: () => this._shell(html`<cos-chat></cos-chat>`) },
		{ path: '/chat', enter: async () => { queueMicrotask(() => this._redirect('/conversations' + window.location.search)); return false; }, render: () => html`` },
		// Stage 67b — time off (Timetastic replacement). Staff-only; the
		// Approvals tab self-gates on the is_manager flag + admin role.
		{ path: '/timeoff', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-timeoff.js')), render: () => this._shell(html`<cos-timeoff .currentUserId=${this._user?.id ?? 0} .userRole=${this._user?.role ?? ''}></cos-timeoff>`) },
		// fleet/training-ui — Training (docs/training-spec.md). Staff-only;
		// wave one has no partner-facing surface.
		{ path: '/training', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-training.js')), render: () => this._shell(html`<cos-training></cos-training>`) },
		{ path: '/training/:key', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-training-course.js')), render: ({ key }) => this._shell(html`<cos-training-course course-key=${key}></cos-training-course>`) },
		{ path: '/training/:key/attempt/:id', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-training-test.js')), render: ({ key, id }) => this._shell(html`<cos-training-test course-key=${key} .attemptId=${Number(id)}></cos-training-test>`) },
		{ path: '/training/:key/results/:id', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-training-results.js')), render: ({ key, id }) => this._shell(html`<cos-training-results course-key=${key} .attemptId=${Number(id)}></cos-training-results>`) },
		{ path: '/admin/training', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-training.js')), render: () => this._shell(html`<cos-admin-training></cos-admin-training>`) },
		// Stage 55 — Contacts (the people at our partners). Staff-only; both
		// list + detail lazy-load. /contacts/:id is a focused read-only profile
		// (no dashboard grid, no per-user layout, no view-as filter).
		// Big Arc Sub-arc 1 — the unified task queue (staff-only, Level-gated API).
		{ path: '/tasks', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-tasks-page.js')), render: () => this._shell(html`<cos-tasks-page></cos-tasks-page>`) },
		{ path: '/contacts', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-contacts-list.js')), render: () => this._shell(html`<cos-contacts-list></cos-contacts-list>`) },
		{ path: '/contacts/:id', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-contact-detail.js')), render: ({ id }) => this._shell(html`<cos-contact-detail .contactId=${Number(id)} .userLevel=${this._user?.level ?? 1} .userRole=${this._user?.role ?? ''}></cos-contact-detail>`) },
		// Stage 60e — IMLS peer comparison. Both shapes resolve to the same page:
		// by partner (resolves the partner's linked fscskey server-side) or by
		// fscskey (the prospect path). Staff-only.
		{ path: '/peer-comparison/partner/:id', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-peer-comparison.js')), render: ({ id }) => this._shell(html`<cos-peer-comparison .partnerId=${Number(id)}></cos-peer-comparison>`) },
		{ path: '/peer-comparison/fscskey/:fscskey', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-peer-comparison.js')), render: ({ fscskey }) => this._shell(html`<cos-peer-comparison .fscskey=${String(fscskey)}></cos-peer-comparison>`) },
		{ path: '/tickets',       enter: this._lazyEnter(this._authGuard, () => import('./components/cos-ticket-list.js')), render: () => this._shell(this._whenUserKnown(() => html`<cos-ticket-list .userLevel=${this._user!.level} .currentUserId=${this._user!.id ?? 0}></cos-ticket-list>`)) },
		// Stage 19e (audit issue 005): /tickets/new is partner-portal
		// only. Level-1 staff get redirected to /tickets with a toast
		// instead of seeing the "use the API directly" dead-end copy.
		// TODO(stage-22+): build a proper staff-side ticket-creation
		// flow (assignee picker, project linking, internal-only flag).
		{
			path: '/tickets/new',
			// Stage 34c — lazy-load cos-ticket-create. The level-1
			// redirect guard wraps as the _lazyEnter guard arg; the
			// chunk only fetches when a level-3 user actually reaches
			// the form. Level-1 staff redirect away before the loader
			// fires, so no wasted chunk download.
			// Stage 44d — staff are now allowed in. The previous
			// guard bounced level-1 with a toast ("file via the
			// API or use Freshdesk"); 44d's template surface
			// gives staff a real form. Standard auth-guard now.
			// Marcie Round 2 (Phase C) — staff-only now: the rich staff create
			// form. Partner-tier create returns to Big Arc Sub-arc 2 (the
			// mode="portal" path re-opens this route); until then the backend
			// gates POST /tickets with RequireLevel(1) and this guard redirects
			// partner-tier away cleanly.
			// Round 6: a library user raises a ticket here too, with a form of their own
			// (cos-ticket-create picks it by level), so the ordinary signed-in guard.
			enter: this._lazyEnter(
				this._authGuard,
				() => import('./components/cos-ticket-create.js'),
			),
			render: () => this._shell(this._whenUserKnown(() => html`<cos-ticket-create .userLevel=${this._user!.level}></cos-ticket-create>`)),
		},
		{ path: '/tickets/mine',  enter: this._lazyEnter(this._authGuard, () => import('./components/cos-ticket-list.js')), render: () => this._shell(this._whenUserKnown(() => html`<cos-ticket-list heading="My tickets" .lockedAssigneeId=${this._user!.id ?? 0} .userLevel=${this._user!.level}></cos-ticket-list>`)) },
		// Round 8: the support mailbox, for Communico staff only (a library user is turned away by
		// the guard before anything is drawn or asked). A literal route, so it sits before
		// /tickets/:id, which would otherwise take "support-email" for a ticket id.
		...(SUPPORT_EMAIL_PAGE_ENABLED ? [{
			path: '/tickets/support-email',
			enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-support-email-page.js')),
			render: () => this._shell(this._whenUserKnown(() => html`<cos-support-email-page .userLevel=${this._user!.level}></cos-support-email-page>`)),
		}] : []),
		// Stage 34c — /tickets/:id is the most-deep-linked staff route
		// but also the heaviest eager component (~1200 lines + the
		// cos-ai-triage-panel / cos-ai-draft-card subtree). Lazy via
		// the standard _lazyEnter pattern; cos-app-shell renders
		// instantly while the chunk lands in parallel with the
		// ticket GET request the route would do anyway.
		{ path: '/tickets/:id',   enter: this._lazyEnter(this._authGuard, () => import('./components/cos-ticket-detail.js')), render: ({ id }) => this._shell(this._whenUserKnown(() => html`<cos-ticket-detail .ticketId=${Number(id)} .userLevel=${this._user!.level} .currentUserId=${this._user!.id ?? 0} .currentUserRole=${this._user!.role ?? ''}></cos-ticket-detail>`)) },
		{ path: '/projects',      enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-project-list.js')), render: () => this._shell(html`<cos-project-list></cos-project-list>`) },
		// Stage 78d.3 — canonical project-create surface. Registered before
		// /projects/:id so the literal "new" segment isn't captured by the
		// :id param matcher (same precedent as /deals/board, /implementation/new).
		{ path: '/projects/new',  enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-project-new.js')), render: () => this._shell(html`<cos-project-new></cos-project-new>`) },
		// Stage 68e-7c — opened to external members (authGuard, not
		// staffOnly): a level-3 partner member who is on the project lands
		// on the locked partner portal (cos-project-detail switches to its
		// customer render when userLevel !== 1). The server gates access +
		// projects the data; a non-member 404s.
		{ path: '/projects/:id',  enter: this._lazyEnter(this._authGuard, () => import('./components/cos-project-detail.js')), render: ({ id }) => this._shell(html`<cos-project-detail .projectId=${Number(id)} .currentUserId=${this._user?.id ?? 0} .currentUserRole=${this._user?.role ?? ''} .currentUserEmail=${this._user?.email ?? ''} .userLevel=${this._user?.level ?? 1}></cos-project-detail>`) },
		// Stage 35b — Deals (spec §3.3). Staff-only via the standard
		// _staffOnlyGuard; both list and detail lazy-loaded so the
		// chunks only land for users who navigate to them.
		// Stage 35c — added /deals/board for the sales kanban surface
		// (spec §10A.1). Registered BEFORE /deals/:id so the literal
		// "board" path doesn't get captured by the :id param matcher.
		{ path: '/deals',        enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-deals-list.js')),  render: () => this._shell(html`<cos-deals-list></cos-deals-list>`) },
		{ path: '/deals/board',  enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-deals-board.js')), render: () => this._shell(html`<cos-deals-board></cos-deals-board>`) },
		{ path: '/deals/:id',    enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-deal-detail.js')), render: ({ id }) => this._shell(html`<cos-deal-detail .dealId=${Number(id)}></cos-deal-detail>`) },
		// Stage 8 — Implementation Portal. Level-3 deep-links (no
		// partner_id in URL) resolve to their own partner via claims.
		// Stage 42a — level-1 staff land on the new cross-partner
		// implementation team dashboard (was: redirect to a partners
		// filter). Level-3 still gets cos-implementation-overview
		// (their own partner-scoped portal). The render branch picks
		// the right component based on user level.
		{
			path: '/implementation',
			// bundle-entry — cos-implementation-overview moved to lazy-load
			// too, so the level!==1 branch below now needs its own import
			// (it used to ride in for free via the old eager import).
			enter: this._lazyEnter(
				this._authGuard,
				() => {
					if (this._user?.level === 1) {
						return import('./components/cos-implementation-dashboard.js');
					}
					return import('./components/cos-implementation-overview.js');
				},
			),
			render: () => {
				if (this._user?.level === 1) {
					return this._shell(html`<cos-implementation-dashboard></cos-implementation-dashboard>`);
				}
				return this._shell(html`<cos-implementation-overview .partnerId=${this._user?.partner_id ?? 0} .userLevel=${this._user?.level ?? 1}></cos-implementation-overview>`);
			},
		},
		// Stage 68b → 78d.3 — the customer-onboarding flow folded into the
		// unified /projects/new surface. This route is now a thin redirect
		// that preserves any existing query params (e.g. partner_id) and
		// pins type=customer-onboarding so deep-links/muscle-memory land on
		// the same flow. Kept _staffOnlyGuard so a partner user still bounces
		// to /dashboard. cos-onboarding-new is retired (superseded).
		// Registered before /implementation/:moduleName so the literal "new"
		// path isn't captured by the :moduleName param matcher.
		{
			path: '/implementation/new',
			enter: this._staffOnlyGuard,
			render: () => {
				queueMicrotask(() => {
					const params = new URLSearchParams(window.location.search);
					params.set('type', 'customer-onboarding');
					this._navigate(`/projects/new?${params.toString()}`);
				});
				return html``;
			},
		},
		{ path: '/implementation/:moduleName', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-implementation-module.js')), render: ({ moduleName }) => this._shell(html`<cos-implementation-module .partnerId=${this._user?.partner_id ?? 0} .moduleName=${String(moduleName)} .userLevel=${this._user?.level ?? 1}></cos-implementation-module>`) },
		// Same surface scoped to a specific partner — staff use it from
		// the partner-detail page.
		{ path: '/partners/:id/implementation', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-implementation-overview.js')), render: ({ id }) => this._shell(html`<cos-implementation-overview .partnerId=${Number(id)} .userLevel=${this._user?.level ?? 1}></cos-implementation-overview>`) },
		{ path: '/partners/:id/implementation/:moduleName', enter: this._lazyEnter(this._authGuard, () => import('./components/cos-implementation-module.js')), render: ({ id, moduleName }) => this._shell(html`<cos-implementation-module .partnerId=${Number(id)} .moduleName=${String(moduleName)} .userLevel=${this._user?.level ?? 1}></cos-implementation-module>`) },
		// Stage 9 — Knowledge Base. Reads open to all; writes are
		// staff-only (the editor and versions components no-op for
		// level-3 internally as a defence-in-depth measure on top of
		// the backend gate).
		{ path: '/kb',                enter: this._lazyEnter(this._authGuard, () => import('./components/cos-kb-list.js')), render: () => this._shell(html`<cos-kb-list .userLevel=${this._user?.level ?? 1}></cos-kb-list>`) },
		// Stage 34c — both KB-editor routes lazy-load the same module.
		// Vite + the browser's module cache dedupe the import, so the
		// chunk fetches once on first visit and is cached for the
		// second route. Staff-only write paths; partner users redirect
		// out via the guard before the loader fires.
		{ path: '/kb/new',            enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-kb-editor.js'), 'Loading editor'), render: () => this._shell(html`<cos-kb-editor .userLevel=${this._user?.level ?? 1}></cos-kb-editor>`) },
		{ path: '/kb/:slug/edit',     enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-kb-editor.js'), 'Loading editor'), render: ({ slug }) => this._shell(html`<cos-kb-editor .slug=${String(slug)} .userLevel=${this._user?.level ?? 1}></cos-kb-editor>`) },
		{ path: '/kb/:slug/versions', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-kb-versions.js')), render: ({ slug }) => this._shell(html`<cos-kb-versions .slug=${String(slug)} .userLevel=${this._user?.level ?? 1}></cos-kb-versions>`) },
		{ path: '/kb/:slug',          enter: this._lazyEnter(this._authGuard, () => import('./components/cos-kb-article.js')), render: ({ slug }) => this._shell(html`<cos-kb-article .slug=${String(slug)} .userLevel=${this._user?.level ?? 1} .currentUserId=${this._user?.id ?? 0}></cos-kb-article>`) },
		// Stage 20 — admin panel. Backend gate (RequireAdminRole) is the
		// authoritative check; _adminGuard avoids a 403 round-trip for
		// users who clicked a /admin link without permission.
		// 2026-07-30 (Paul) — /admin used to silently redirect to Users.
		// Neither admin surface should preselect anything now: the nav
		// (cos-admin-nav, rendered by cos-app-shell beside this content
		// whenever the path starts with /admin) shows every group
		// collapsed, and this route just renders a "pick one" prompt —
		// the same wording /partner-admin shows for its own empty case.
		{ path: '/admin',         enter: this._adminGuard, render: () => this._shell(html`<cos-empty-state tone="quiet" title="Choose a category" description="Pick a category from the list on the left to see its settings."></cos-empty-state>`) },
		{ path: '/admin/users',   enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-users.js')), render: () => this._shell(html`<cos-admin-users .currentUserId=${this._user?.id ?? 0}></cos-admin-users>`) },
		{ path: '/admin/audit',   enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-audit.js')), render: () => this._shell(html`<cos-admin-audit></cos-admin-audit>`) },
		// Staff feedback / issue-capture platform. Round 2 (Paul: "chris and
		// kieran should see this too") — the console's reads are now
		// RequireLevel(1) on the backend, so /feedback is reachable by any
		// Communico staff member via _staffOnlyGuard (not just admins). The
		// admin-role deep link keeps working too — both render the SAME
		// <cos-admin-feedback>, gated presentationally via canTriage (the
		// backend still enforces Patch/Rescan as admin-only regardless).
		{ path: '/feedback',       enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-admin-feedback.js')), render: () => this._shell(html`<cos-admin-feedback .canTriage=${isAdmin(this._user)} .canDispatch=${isDispatcher(this._user)} .currentUserId=${this._user?.id ?? 0}></cos-admin-feedback>`) },
		{ path: '/admin/feedback', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-feedback.js')), render: () => this._shell(html`<cos-admin-feedback .canTriage=${isAdmin(this._user)} .canDispatch=${isDispatcher(this._user)} .currentUserId=${this._user?.id ?? 0}></cos-admin-feedback>`) },
		// Stage 66 — team activity analytics (admin: named detail + own-vs-role tab).
		{ path: '/admin/activity', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-activity.js')), render: () => this._shell(html`<cos-admin-activity teamView></cos-admin-activity>`) },
		// Stage 67b — time-off admin curation (holidays, leave types, org chart).
		{ path: '/admin/timeoff', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-timeoff.js')), render: () => this._shell(html`<cos-admin-timeoff></cos-admin-timeoff>`) },
		{ path: '/admin/labels',  enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-labels.js')), render: () => this._shell(html`<cos-admin-labels></cos-admin-labels>`) },
		{ path: '/admin/chat-channels', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-chat-channels.js')), render: () => this._shell(html`<cos-admin-chat-channels></cos-admin-chat-channels>`) },
		{ path: '/admin/module-templates', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-module-templates.js')), render: () => this._shell(html`<cos-admin-module-templates></cos-admin-module-templates>`) },
		{ path: '/admin/kb-article-types', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-kb-article-types.js')), render: () => this._shell(html`<cos-admin-kb-article-types></cos-admin-kb-article-types>`) },
		// Marcie arc 2a-3 — the 7 partner-property lookup admin pages (5 flat via the shared parameterized page + 2 bespoke).
		{ path: '/admin/partner-ils-types', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-partner-lookup.js')), render: () => this._shell(html`<cos-admin-partner-lookup kind="ils"></cos-admin-partner-lookup>`) },
		{ path: '/admin/partner-discovery-layers', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-partner-lookup.js')), render: () => this._shell(html`<cos-admin-partner-lookup kind="discovery"></cos-admin-partner-lookup>`) },
		{ path: '/admin/partner-purchase-types', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-partner-lookup.js')), render: () => this._shell(html`<cos-admin-partner-lookup kind="purchase"></cos-admin-partner-lookup>`) },
		{ path: '/admin/partner-statuses', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-partner-lookup.js')), render: () => this._shell(html`<cos-admin-partner-lookup kind="status"></cos-admin-partner-lookup>`) },
		{ path: '/admin/partner-zoom-config-types', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-partner-lookup.js')), render: () => this._shell(html`<cos-admin-partner-lookup kind="zoom"></cos-admin-partner-lookup>`) },
		{ path: '/admin/partner-currencies', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-partner-lookup.js')), render: () => this._shell(html`<cos-admin-partner-lookup kind="currency"></cos-admin-partner-lookup>`) },
		{ path: '/admin/partner-consortiums', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-partner-consortiums.js')), render: () => this._shell(html`<cos-admin-partner-consortiums></cos-admin-partner-consortiums>`) },
		{ path: '/admin/partner-module-statuses', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-partner-module-statuses.js')), render: () => this._shell(html`<cos-admin-partner-module-statuses></cos-admin-partner-module-statuses>`) },
		{ path: '/admin/project-phases',     enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-project-phases.js')), render: () => this._shell(html`<cos-admin-project-phases></cos-admin-project-phases>`) },
		// Stage 68b — project template admin editor.
		{ path: '/admin/project-templates', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-project-templates.js')), render: () => this._shell(html`<cos-admin-project-templates></cos-admin-project-templates>`) },
		{ path: '/admin/escalation-rules', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-escalation-rules.js')), render: () => this._shell(html`<cos-admin-escalation-rules></cos-admin-escalation-rules>`) },
		{ path: '/admin/derivation-rules', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-derivation-rules.js')), render: () => this._shell(html`<cos-admin-derivation-rules></cos-admin-derivation-rules>`) },
		{ path: '/admin/ticket-templates', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-ticket-templates.js')), render: () => this._shell(html`<cos-admin-ticket-templates></cos-admin-ticket-templates>`) },
		{ path: '/admin/custom-fields',    enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-custom-fields.js')),    render: () => this._shell(html`<cos-admin-custom-fields></cos-admin-custom-fields>`) },
		{ path: '/admin/preserve-limits',  enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-preserve-limits.js')), render: () => this._shell(html`<cos-admin-preserve-limits></cos-admin-preserve-limits>`) },
		{ path: '/admin/preserve-ia-import',  enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-preserve-admin-ia-import.js')), render: () => this._shell(html`<cos-preserve-admin-ia-import></cos-preserve-admin-ia-import>`) },
		{ path: '/admin/client-settings',  enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-client-settings.js')), render: () => this._shell(html`<cos-admin-client-settings></cos-admin-client-settings>`) },
		{ path: '/admin/schema-explorer',  enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-schema-explorer.js')), render: () => this._shell(html`<cos-admin-schema-explorer></cos-admin-schema-explorer>`) },
		{ path: '/admin/mcp-library-tools', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-mcp-library-tools.js')), render: () => this._shell(html`<cos-admin-mcp-library-tools></cos-admin-mcp-library-tools>`) },
		{ path: '/admin/data-console',     enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-data-console.js')), render: () => this._shell(html`<cos-admin-data-console></cos-admin-data-console>`) },
		{ path: '/admin/pls/upload',       enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-pls-upload.js')), render: () => this._shell(html`<cos-admin-pls-upload></cos-admin-pls-upload>`) },
			{ path: '/admin/pls/link-suggestions', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-pls-link-suggestions.js')), render: () => this._shell(html`<cos-admin-pls-link-suggestions></cos-admin-pls-link-suggestions>`) },
		// Stage 46a — design system storybook + admin design settings.
		// The storybook is unguarded-by-content but admin-only by route
		// (visual reference for staff). The settings page is the live
		// theme editor.
		// Third-party OAuth connections (QuickBooks today). Super-admin is
		// enforced SERVER side on all three endpoints; the route guard is the
		// ordinary admin one so a non-super-admin gets the honest 403 message
		// rather than a not-found.
		{ path: '/admin/integrations', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-integrations.js')), render: () => this._shell(html`<cos-admin-integrations></cos-admin-integrations>`) },
		// Agent platform (PRD 11): the list, the create form, and one agent's
		// edit/setup page. /new is registered BEFORE /:id so it is never
		// swallowed as id="new".
		{ path: '/admin/agents', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-agents.js')), render: () => this._shell(html`<cos-admin-agents></cos-admin-agents>`) },
		{ path: '/admin/agents/new', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-agent-detail.js')), render: () => this._shell(html`<cos-admin-agent-detail .agentId=${0}></cos-admin-agent-detail>`) },
		{ path: '/admin/agents/:id', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-agent-detail.js')), render: ({ id }) => this._shell(html`<cos-admin-agent-detail .agentId=${Number(id)}></cos-admin-agent-detail>`) },
		// Mission playbooks (agent missions M5, PRD 14.4): admin only.
		// Decision models (agent workflows W2, PRD 17.7).
		{ path: '/admin/decision-engines', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-decision-engines.js')), render: () => this._shell(html`<cos-admin-decision-engines></cos-admin-decision-engines>`) },
		// Workflow definitions and the canvas editor (agent workflows W5, PRD 17.3).
		{ path: '/admin/workflows', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-workflows.js')), render: () => this._shell(html`<cos-admin-workflows></cos-admin-workflows>`) },
		{ path: '/admin/workflows/:id', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-workflow-editor.js')), render: ({ id }) => this._shell(html`<cos-workflow-editor .definitionId=${Number(id)}></cos-workflow-editor>`) },
		// The GitHub App (agent workflows W4): the repositories COS reviews, and
		// where GitHub sends a person back after Connect GitHub.
		{ path: '/admin/github', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-github.js')), render: () => this._shell(html`<cos-admin-github></cos-admin-github>`) },
		{ path: '/settings/github', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-github-callback-page.js')), render: () => this._shell(html`<cos-github-callback-page></cos-github-callback-page>`) },
		{ path: '/admin/agent-playbooks', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-agent-playbooks.js')), render: () => this._shell(html`<cos-admin-agent-playbooks></cos-admin-agent-playbooks>`) },
		{ path: '/admin/agent-playbooks/new', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-agent-playbook-detail.js')), render: () => this._shell(html`<cos-admin-agent-playbook-detail .playbookId=${0}></cos-admin-agent-playbook-detail>`) },
		{ path: '/admin/agent-playbooks/:id', enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-agent-playbook-detail.js')), render: ({ id }) => this._shell(html`<cos-admin-agent-playbook-detail .playbookId=${Number(id)}></cos-admin-agent-playbook-detail>`) },
		// The agent approval page (PRD 7.5): any staff user a proposal names; the API decides who may see it.
		{ path: '/agent-approvals/:id', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-agent-approval-page.js')), render: ({ id }) => this._shell(html`<cos-agent-approval-page .proposalId=${String(id)}></cos-agent-approval-page>`) },
		// My repeating tasks (agent missions M1): staff only; the API shows each person their own.
		{ path: '/agent-schedules', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-agent-schedules-page.js')), render: () => this._shell(html`<cos-agent-schedules-page></cos-agent-schedules-page>`) },
		// Missions (agent missions M2): staff only; the API shows each person their own, an admin anyone's.
		{ path: '/agent-missions', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-agent-missions-page.js')), render: () => this._shell(html`<cos-agent-missions-page></cos-agent-missions-page>`) },
		{ path: '/agent-missions/:id', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-agent-mission-detail.js')), render: ({ id }) => this._shell(html`<cos-agent-mission-detail .missionId=${String(id)}></cos-agent-mission-detail>`) },
		// Workflow runs (agent workflows W1): read-only list and timeline.
		{ path: '/workflow-inbox', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-workflow-inbox-page.js')), render: () => this._shell(html`<cos-workflow-inbox-page></cos-workflow-inbox-page>`) },
		{ path: '/workflow-runs', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-workflow-runs-page.js')), render: () => this._shell(html`<cos-workflow-runs-page></cos-workflow-runs-page>`) },
		{ path: '/workflow-runs/:id', enter: this._lazyEnter(this._staffOnlyGuard, () => import('./components/cos-workflow-run-detail.js')), render: ({ id }) => this._shell(html`<cos-workflow-run-detail .runId=${String(id)}></cos-workflow-run-detail>`) },
		{ path: '/admin/design-system',    enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-design-system.js')), render: () => this._shell(html`<cos-admin-design-system></cos-admin-design-system>`) },
		{ path: '/admin/design-settings',  enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-admin-design-settings.js')), render: () => this._shell(html`<cos-admin-design-settings></cos-admin-design-settings>`) },
		{ path: '/admin/triage-review',    enter: this._lazyEnter(this._adminGuard, () => import('./components/cos-triage-review-page.js')), render: () => this._shell(html`<cos-triage-review-page></cos-triage-review-page>`) },
		// Stage 94a — DEV-only smoke harness for the cos-record-shell foundation
		// primitive (consumer-less this stage). import.meta.env.DEV is statically
		// false in the prod container build, so this route + its lazy import are
		// tree-shaken out (the Stage 19g convention). 94b is the first real consumer.
		...(import.meta.env.DEV
			? [
					{
						path: '/dev/record-shell',
						enter: this._lazyEnter(this._authGuard, () => import('./components/cos-record-shell-demo.js')),
						render: () => this._shell(html`<cos-record-shell-demo></cos-record-shell-demo>`),
					},
				]
			: []),
		{ path: '/*',             enter: this._authGuard, render: () => this._shell(html`<cos-not-found></cos-not-found>`) },
	]);

	static styles = css`
		:host { display: block; min-height: 100vh; }

		/* Stage 41b-2.1 — small bottom-right pill overlay while a
		   lazy route's chunk fetches. Positioned over the route
		   that's still painted (router holds the old view until
		   the new one's enter resolves), so it reads as "the new
		   page is loading" rather than blocking a blank screen. */
		.chunk-loading {
			position: fixed;
			right: 24px;
			bottom: 24px;
			z-index: 100;
			display: inline-flex;
			align-items: center;
			gap: 10px;
			padding: 10px 18px;
			background: var(--cos-charcoal, #1F2937);
			color: var(--cos-surface, #FFFFFF);
			border-radius: 999px;
			box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
			font-family: var(--cos-font-family);
			font-size: 14px;
			animation: chunk-loading-fade 160ms var(--cos-ease-out, cubic-bezier(0.23, 1, 0.32, 1));
		}
		.chunk-spinner {
			display: inline-block;
			width: 16px; height: 16px;
			border: 2px solid rgba(255, 255, 255, 0.32);
			border-top-color: var(--cos-surface, #FFFFFF);
			border-radius: 50%;
			animation: chunk-spin var(--cos-spinner-duration, 700ms) linear infinite;
		}
		@keyframes chunk-spin {
			to { transform: rotate(360deg); }
		}
		@keyframes chunk-loading-fade {
			from { opacity: 0; transform: translateY(4px); }
			to { opacity: 1; transform: translateY(0); }
		}
		@media (prefers-reduced-motion: reduce) {
			.chunk-spinner { animation: none; }
			.chunk-loading { animation: none; }
		}

		/* Proactive "new version available" banner. TOP-CENTRE, because
		   bottom-left put it over the dark nav rail where it was routinely
		   missed — and a missed prompt means someone spends the day on stale
		   code being told their bug is fixed. Sits above module overlays
		   (z 60/61) and the feedback FAB (98) so it is never buried. Tokens
		   (with fallbacks) so it themes correctly in light + dark. */
		.update-banner {
			position: fixed;
			left: 50%;
			transform: translateX(-50%);
			top: 12px;
			z-index: 200;
			display: inline-flex;
			align-items: center;
			gap: 12px;
			max-width: min(92vw, 460px);
			padding: 12px 12px 12px 18px;
			background: var(--cos-surface, #FFFFFF);
			color: var(--cos-text-primary, #1F2937);
			border: 1px solid var(--cos-border, #E5E7EB);
			border-left: 3px solid var(--cos-brand-500, #19B4C7);
			border-radius: var(--cos-radius-md, 8px);
			box-shadow: 0 8px 24px var(--cos-shadow-lg, rgba(0, 0, 0, 0.18));
			font-family: var(--cos-font-family);
			font-size: 14px;
			animation: update-banner-in 200ms var(--cos-ease-out, cubic-bezier(0.23, 1, 0.32, 1));
		}
		/* Own keyframe, not chunk-loading-fade: an animated transform replaces
		   the static translateX(-50%) centring, so it must be restated here
		   or the banner paints off-centre and snaps back at the end. */
		@keyframes update-banner-in {
			from { opacity: 0; transform: translateX(-50%) translateY(-6px); }
			to { opacity: 1; transform: translateX(-50%) translateY(0); }
		}
		@keyframes update-banner-fade {
			from { opacity: 0; }
			to { opacity: 1; }
		}
		.update-msg { flex: 1; }
		.update-reload {
			flex: none;
			cursor: pointer;
			border: none;
			border-radius: var(--cos-radius-sm, 6px);
			padding: 6px 14px;
			background: var(--cos-brand-500, #19B4C7);
			color: var(--cos-text-on-color, #FFFFFF);
			font: inherit;
			font-weight: 600;
		}
		.update-reload:focus-visible { outline: 2px solid var(--cos-brand-700, #0E7A88); outline-offset: 2px; }
		.update-dismiss {
			flex: none;
			cursor: pointer;
			border: none;
			background: none;
			color: var(--cos-text-tertiary, #6B7280);
			font-size: 20px;
			line-height: 1;
			padding: 0 4px;
		}
		.update-dismiss:hover { color: var(--cos-text-primary, #1F2937); }
		@media (prefers-reduced-motion: reduce) {
			.update-banner { animation: update-banner-fade 200ms var(--cos-ease-out, cubic-bezier(0.23, 1, 0.32, 1)); }
		}
	`;

	connectedCallback(): void {
		// Kick off /auth/me BEFORE super so the Router's hostConnected hook
		// (which fires inside super.connectedCallback) sees a populated
		// _userPromise on the very first route match. Without this, the
		// initial route renders with _user=null and child components fall
		// back to level=1, tripping 403s on staff-only endpoints during
		// a deep-link refresh.
		if (api.isAuthenticated()) {
			this._userPromise = this._restoreUser();
		}

		// Stage 85a — apply the OS-default Quiet theme SYNCHRONOUSLY before
		// first paint so the correct light/dark mode lands even pre-auth
		// (login page) and there's no wrong-mode flash. The stored per-user
		// theme (if any) is applied after /auth/me + preferences resolve
		// (_applyStoredTheme), overriding this default.
		applyUserTheme(osDefaultTheme());

		// Stage 46a — boot the runtime theme as early as possible so any
		// admin-tuned tokens land on :root before the first route paints.
		// `bootstrapTheme` soft-fails (returns {} on network errors), so
		// the app never blocks on it; the cascade keeps defaults from
		// tokens.css in place if the fetch never resolves.
		if (api.isAuthenticated()) {
			void bootstrapTheme();
			void this._applyStoredTheme();
		}
		bindThemeChangeListener();

		// MUST precede super.connectedCallback(): that connects the Router
		// controller, whose own popstate listener calls goto() and unmounts the
		// page. Listeners on the same target fire in registration order, so
		// registering first is what lets _onPopState stopImmediatePropagation()
		// and keep the current page mounted when the guard blocks a back-nav.
		window.addEventListener('popstate', this._onPopState);

		super.connectedCallback();

		// _applyLibraryFromURL() is NOT called here. super.connectedCallback()
		// (just above) is what connects the Router and drives its first
		// enter() for whatever route this boot lands on — an unawaited call
		// fired right after it would run concurrently with that enter(), with
		// nothing forcing one to finish before the other. The route's own
		// guard (_authGuard/_adminGuard/_staffOnlyGuard, or /partners' bespoke
		// enter) calls _applyLibraryFromURL() itself, as its own last step
		// before resolving `true` — see that method's header comment.

		window.addEventListener('cos:auth:login', this._onLogin as unknown as EventListener);
		window.addEventListener('cos:auth:logout', this._onLogout);
		window.addEventListener('cos:auth:expired', this._onLogout);
		window.addEventListener(LEGACY_CLIENT_EVENT, this._onLegacyClientChanged as EventListener);
		window.addEventListener(CARDHOLDER_TERM_CHANGED_EVENT, this._onCardholderTermChanged);
		window.addEventListener(DATE_ORDER_CHANGED_EVENT, this._onDateOrderChanged);

		// Intercept anchor clicks so router.goto() handles internal navigation.
		this.addEventListener('click', this._interceptLink as EventListener);
		// One app-level beforeunload covers browser refresh / tab-close /
		// external navigation for every surface registered with the
		// navigation guard (each surface no longer wires its own).
		window.addEventListener('beforeunload', this._onBeforeUnload);
		// Stage 71 — command palette (+ other non-anchor callers) request
		// client-side navigation through this event; route it the same way.
		window.addEventListener('cos:navigate', this._onNavigateEvent as EventListener);

		// Proactive stale-deploy detection. Capture the entry-chunk hash
		// this document booted with; poll for a newer one on an interval +
		// on tab refocus. No-ops in dev (no hashed entry → _bootHash null).
		this._bootHash = currentEntryHash(document);
		if (this._bootHash) {
			this._versionTimer = window.setInterval(() => this._maybeCheckVersion(0), VERSION_POLL_MS);
			document.addEventListener('visibilitychange', this._onVisibleCheckVersion);
		}

		// Responsive Workstream C — register the Roam service worker.
		// Fire-and-forget: registration is a progressive enhancement that
		// nothing else in the app waits on, and it no-ops entirely outside
		// import.meta.env.PROD (dev has HMR, no stale-SW problem).
		void registerServiceWorker();
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();
		window.removeEventListener('cos:auth:login', this._onLogin as unknown as EventListener);
		window.removeEventListener('cos:auth:logout', this._onLogout);
		window.removeEventListener('cos:auth:expired', this._onLogout);
		window.removeEventListener(LEGACY_CLIENT_EVENT, this._onLegacyClientChanged as EventListener);
		window.removeEventListener(CARDHOLDER_TERM_CHANGED_EVENT, this._onCardholderTermChanged);
		window.removeEventListener(DATE_ORDER_CHANGED_EVENT, this._onDateOrderChanged);
		window.removeEventListener('popstate', this._onPopState);
		window.removeEventListener('cos:navigate', this._onNavigateEvent as EventListener);
		window.removeEventListener('beforeunload', this._onBeforeUnload);
		if (this._versionTimer !== null) window.clearInterval(this._versionTimer);
		document.removeEventListener('visibilitychange', this._onVisibleCheckVersion);
	}

	private _onVisibleCheckVersion = () => {
		if (document.visibilityState === 'visible') this._maybeCheckVersion(VERSION_MIN_GAP_MS);
	};

	// Throttled version poll. Skips only if a check ran within minGapMs or
	// one is already in flight — deliberately NOT "once an update is known",
	// because a second deploy while the first prompt is still showing (or
	// was dismissed) has to be able to surface too.
	private _maybeCheckVersion(minGapMs: number): void {
		if (!this._bootHash || this._versionInFlight) return;
		const now = Date.now();
		if (now - this._lastVersionCheck < minGapMs) return;
		this._lastVersionCheck = now;
		void this._checkForUpdate();
	}

	private async _checkForUpdate(): Promise<void> {
		this._versionInFlight = true;
		try {
			// cache:no-store so we see the deployed index.html, not a cached
			// copy. Same-origin; a failure (offline) just leaves it for the
			// next poll — the reactive chunk recovery is the backstop.
			const res = await fetch(`${window.location.origin}/index.html`, { cache: 'no-store' });
			if (!res.ok) return;
			const latest = parseEntryHash(await res.text());
			if (isUpdateAvailable(this._bootHash, latest)) {
				this._latestHash = latest;
				// Keep polling. A newer build than the one they dismissed must
				// still be able to raise the prompt again — that is the whole
				// point of tracking the dismissed hash rather than a boolean.
				this._updateAvailable = true;
			} else {
				// Back in step with the server (only really happens on a
				// rollback) — clear so a future deploy prompts cleanly.
				this._latestHash = null;
				this._updateAvailable = false;
			}
		} catch {
			/* offline / transient — try again next poll */
		} finally {
			this._versionInFlight = false;
		}
	}

	private _onBeforeUnload = (e: BeforeUnloadEvent): void => {
		if (navigationGuard.hasBlockers()) {
			e.preventDefault();
			e.returnValue = '';
		}
	};

	private _onNavigateEvent = (e: Event) => {
		const href = (e as CustomEvent<{ href: string }>).detail?.href;
		if (href) this._navigate(href);
	};

	private _onPopState = (e: PopStateEvent) => {
		// popstate fires AFTER the browser has already changed the URL, so a
		// guard can't pre-empt it — if the user declines to leave, push the
		// committed location back to undo the back/forward. (Standard SPA
		// beforeunload-guard pattern; the extra history entry is accepted.)
		if (!navigationGuard.confirmLeave()) {
			// Restoring the URL is not enough: @lit-labs/router listens to
			// popstate too and would goto() the popped location, unmounting the
			// page whose unsaved work the user just chose to keep. This handler
			// is registered before the Router's (see connectedCallback), so
			// stopping immediate propagation keeps the event from ever reaching
			// it — the page stays mounted and the edits survive.
			e.stopImmediatePropagation();
			// The trail's arrival state (docs/design-system/breadcrumb-trail.md
			// §5): every pushState records where the user was standing when it
			// ran, so the shell can offer the page they actually came from as
			// the parent crumb. Captured BEFORE the URL moves.
			const restoreFrom = window.location.pathname + window.location.search;
			window.history.pushState({ from: restoreFrom }, '', this._committedHref);
			return;
		}
		// Back or Forward: the page being left is the one last committed (the title is
		// still its own, the new route has not drawn yet).
		rememberAddress(window.location.origin + this._committedHref, document.title);
		this._committedHref = window.location.pathname + window.location.search;
		this._path = window.location.pathname;
	};

	private _onLogin = async (e: CustomEvent) => {
		// Stage 27 — cos-login dispatches {token, expires_in}; the user
		// row lives behind /auth/me which the bearer-injecting api
		// client picks up. We fetch it after the token lands so the
		// shell can render with the user's level + partner_id.
		api.setToken(e.detail.token);
		if (e.detail.expires_in) {
			try {
				sessionStorage.setItem('cos.expires_at', String(Date.now() + e.detail.expires_in * 1000));
			} catch { /* noop */ }
		}
		try {
			this._user = (await api.getMe()) as User;
		} catch {
			// /auth/me failed despite a fresh token — push back to login
			// rather than render a half-bootstrapped shell.
			api.clearToken();
			this._navigate('/login');
			return;
		}
		// Stage 46a — refresh the theme once we have a valid bearer.
		// First-load may have skipped this (no token yet); on fresh
		// login we want admin overrides to land before the dashboard
		// renders.
		void bootstrapTheme();
		void this._applyStoredTheme();
		// Honor any captured destination from the pre-login navigation.
		let dest = '/dashboard';
		try {
			const saved = sessionStorage.getItem(LOGIN_REDIRECT_KEY);
			if (saved) {
				dest = saved;
				sessionStorage.removeItem(LOGIN_REDIRECT_KEY);
			}
		} catch { /* noop */ }
		this._navigate(dest);
	};

	private _onLogout = () => {
		api.clearToken();
		clearCachedMe();
		// Forget the chosen library too (Paul, 2026-07-29). The pick is
		// session-scoped so flipping workspaces does not re-ask, but it
		// must NOT survive a sign-out — sessionStorage would otherwise
		// leave the next person in the same tab looking at a library they
		// never chose.
		clearLegacyClient();
		this._user = null;
		// The session is gone — any surface's unsaved work can no longer be
		// saved, so drop the guards rather than trap the forced redirect.
		navigationGuard.clear();
		this._navigate('/login');
	};

	// Stage 85a — fetch the per-user Quiet theme and apply it, overriding
	// the OS default applied synchronously at connect. If the user has no
	// stored theme yet, persist the resolved OS default once (silent — no
	// prompt), so the next load hits the stored fast path. Soft-fails: if
	// preferences are unavailable the OS default already on :root stands.
	private async _applyStoredTheme(): Promise<void> {
		try {
			const prefs = (await api.getMyPreferences()) as Record<string, unknown>;
			if (prefs && prefs.theme != null) {
				applyUserTheme(normalizeTheme(prefs.theme));
			} else {
				const def = osDefaultTheme();
				applyUserTheme(def);
				try { await api.setMyPreference('theme', def); } catch { /* noop */ }
			}
			// Stage 96 — apply the sidebar width from the same prefs blob. Absent →
			// compact (the token fallback gives the pre-96 260px; zero breakage).
			if (isSidebarWidth(prefs?.sidebar_width)) applySidebarWidth(prefs.sidebar_width as SidebarWidth);
		} catch { /* preferences unavailable — keep the OS default */ }
	}

	private async _restoreUser(): Promise<void> {
		try {
			this._user = (await api.getMe()) as User;
			writeCachedMe(this._user);
		} catch (err) {
			// No answer at all (an outage, a reload with no connection) is not a rejected token: keep
			// the sign-in and carry on as the user /auth/me last named, so Desk can start offline.
			if (isConnectionFailure(err)) {
				this._user = readCachedMe<User>() ?? this._user;
				return;
			}
			// token rejected; clear and bounce to login
			clearCachedMe();
			api.clearToken();
			if (window.location.pathname !== '/login') {
				this._navigate('/login');
			}
		}
	}

	// A redirect replaces the address it was reached at instead of adding to
	// history, so Back goes to the page before it, not to the stub that sends
	// the user forward again.
	private _redirect(href: string): void {
		this._navigate(href, false, true);
	}

	private _navigate(href: string, guardAnswered = false, replace = false) {
		if (href === window.location.pathname + window.location.search) return;
		// A surface with unsaved work (registered with the navigation guard)
		// gets a confirm before we leave; declining aborts the route change
		// entirely (no pushState, no router.goto). A surface that registered
		// a styled dialog is asked asynchronously and the navigation resumes
		// on its answer; otherwise the synchronous confirm applies.
		if (!guardAnswered) {
			const pending = navigationGuard.presentLeave();
			if (pending) {
				void pending.then((leave) => { if (leave) this._navigate(href, true, replace); });
				return;
			}
			if (!navigationGuard.confirmLeave()) return;
		}
		// Leaving a failed route clears its error frame (covers eager
		// routes too, which never reach _lazyEnter's success path).
		this._chunkError = false;
		// Navigation is the moment right before a dead chunk would bite —
		// a cheap throttled check catches a fresh deploy just in time.
		this._maybeCheckVersion(VERSION_MIN_GAP_MS);
		const prevPath = window.location.pathname;
		// See the popstate site above: `from` is the page being LEFT, read
		// before pushState moves the URL. cos-trail reads it off
		// history.state to pick an alternate parent crumb.
		const from = window.location.pathname + window.location.search;
		if (replace) {
			// The stub being replaced is not a place to return to, so it is not
			// remembered, and the entry keeps the page it was reached from.
			const priorFrom = (window.history.state as { from?: string } | null)?.from;
			window.history.replaceState({ from: priorFrom ?? from }, '', href);
		} else {
			rememberAddress(window.location.href, document.title);
			window.history.pushState({ from }, '', href);
		}
		this._committedHref = href;
		// Stage 19e — pass the path-only to the router; the query
		// string lives in window.location.search and is read by
		// downstream components. Passing the full href confused
		// @lit-labs/router's path matcher (the ?has_modules… suffix
		// prevented the /partners route from matching).
		//
		// Feedback #10 — a hash suffix needs the same treatment (see
		// utils/routerPath.ts): left in, it corrupts a `:id` route param
		// and the destination page fetches e.g. GET /tickets/NaN — the
		// generic error copy a task notification's `#tasks` link was
		// hitting. The browser's address bar and `location.hash` are
		// already correct from pushState(href) above, so the hashchange
		// dispatch below still sees the real hash.
		const pathOnly = routerPathOnly(href);
		this._path = pathOnly;
		this._router.goto(pathOnly);
		this.requestUpdate();
		// Marcie R7 (1h) — "it randomly jumps you to different places on the ticket
		// screen vs the top when you enter the ticket."
		// Root cause: the SPA never reset scroll on navigation, so the offset from
		// the PREVIOUS page carried over — scroll the ticket list halfway down,
		// click a ticket, and the detail opens mid-page. The "randomness" is just
		// however far the previous page was scrolled.
		// Forward navigation only: popstate (back/forward) is deliberately NOT
		// touched, so the browser's own scroll restoration still returns you to
		// where you were. A hash href is left alone too — that's a deep-link, and
		// utils/sectionAnchors owns positioning it.
		if (pathOnly !== prevPath && !href.includes('#')) {
			window.scrollTo({ top: 0, behavior: 'auto' });
		}
		// Same-path query navigation (e.g. /kb → /kb?view=partner) never
		// re-mounts the page, so pages that key state on the query string
		// can't see it happened. Signal them (92c convention: same-path
		// query nav is the PAGE's responsibility — this is its hook).
		if (pathOnly === prevPath) {
			window.dispatchEvent(new CustomEvent('cos:querychange'));
		}
		// Feedback #10 — a hash-suffixed href (e.g. a notification's
		// `/tickets/276342#tasks`) landing on a page that's ALREADY mounted
		// (same route, or the router reusing the same element for a changed
		// :id — Lit updates the property in place rather than remounting)
		// never fires a real 'hashchange': pushState() deliberately does not
		// dispatch one, and connectedCallback (the section-anchor handler's
		// other trigger, see utils/sectionAnchors.ts) doesn't re-run either.
		// The result is a click that updates the URL bar but visibly does
		// nothing — cos-ticket-detail's own "jump to internal notes" action
		// already works around this per-call (history.replaceState +
		// handleHashChange('ticket')); dispatching the real event here fixes
		// it generically for every hash deep-link, current and future,
		// through the same listener sectionAnchors already registers.
		if (href.includes('#')) {
			window.dispatchEvent(new Event('hashchange'));
		}
	}

	private _interceptLink = (e: MouseEvent) => {
		if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
		const path = e.composedPath();
		const anchor = path.find(n => n instanceof HTMLAnchorElement) as HTMLAnchorElement | undefined;
		if (!anchor) return;
		const href = anchor.getAttribute('href');
		if (!href) return;
		// External / mailto / hash / new-tab targets: let the browser handle it.
		if (anchor.target === '_blank') return;
		// A file download (the shared dashboard's PDF) is not an in-app route.
		if (anchor.hasAttribute('download')) return;
		if (/^([a-z]+:)?\/\//i.test(href)) return;
		if (href.startsWith('mailto:') || href.startsWith('#')) return;
		e.preventDefault();
		this._navigate(href);
	};

	render() {
		// Stage 41b-2.1 — when a lazy route's chunk is in flight,
		// overlay a small "Loading …" indicator on top of whatever the
		// router is rendering (the previous page until enter resolves).
		// Falls through to `nothing` when idle so prod renders are
		// allocation-free in the steady state.
		// A twice-failed route chunk leaves the router with nothing to
		// render on a direct load, so own the frame here rather than
		// let it paint blank.
		if (isUnlinkedLibraryLogin(this._user) && this._path !== '/login') {
			// Lazy, so every other user pays nothing for it in the entry chunk.
			void import('./components/cos-library-not-linked.js');
			return html`<cos-library-not-linked .email=${this._user?.email ?? ''}></cos-library-not-linked>`;
		}
		if (this._chunkError) {
			return this._shell(html`
				<cos-error-state
					heading="Couldn't open this page"
					description="The app may have been updated while this tab was open, or the connection dropped. Reloading usually fixes it."
					cta="retry"
					@cos:error:retry=${() => window.location.reload()}></cos-error-state>
			`);
		}
		return html`
			${this._router.outlet()}
			${this._chunkLoading ? html`
				<div class="chunk-loading" role="status" aria-live="polite">
					<span class="chunk-spinner" aria-hidden="true"></span>
					<span>${this._chunkLoading}…</span>
				</div>
			` : nothing}
			${shouldPromptReload(this._bootHash, this._latestHash, this._dismissedHash) ? html`
				<div class="update-banner" role="status" aria-live="polite">
					<span class="update-msg">A new version of CommunicoOS is available. Reload to get the latest fixes.</span>
					<button class="update-reload" @click=${() => window.location.reload()}>Reload</button>
					<button class="update-dismiss" aria-label="Dismiss"
						@click=${() => {
						this._dismissedHash = this._latestHash;
						saveDismissedHash(this._dismissedHash);
					}}>&times;</button>
				</div>
			` : nothing}
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'cos-app': CosApp;
	}
}
